// Baker-2 ways AS DRAWN (APP_BAKER §4 "The ground under a way (WAYS-G)"): the ONE lane-ribbon geometry of
// a `ways.json` (format vrm-ways). The client draws it (client/src/ways2.js),
// the WORLD stage clips it at solid footprints (world/stage.mjs writes the
// clip into ways.json), the gates tap it (scripts/check-ways.mjs burial rows
// on a baker-2 bake). PURE module shared by the WebXR client (esbuild import)
// and node (require) — dependency-free: no three, no fs, no JSON imports.
//
// The ribbon is the corridor's lane (world/corridor.js stationTables): per
// SURFACE corridor station (flag 0) the cross-section y + cf·o over
// |o| <= w/2 (stairs: the TREAD, their drawn / corridor surface), o along the
// station normal (game/solids.js perps rule; a run joined to the next run of
// its chain at a shared station takes the chain's normal there, so the two
// ribbons meet on one cross-section). The terrain under it is that lane minus
// the class draw offset (params draw_offset_m) — the ground IS the lane, the
// offset is the z-fight margin. Two vertices per cross-section (the lane is
// linear in o), two triangles per station gap, diagonal hi(k) -> lo(k+1)
// (roads3d mergeRibbons' winding); `tri()` is that triangle split, so a gate
// tap lies on the triangle the GPU draws.
//
// Not drawn: deck and bore stations (flag 1) and every gap touching one
// (their structures are S6/S7); a station whose lane is blocked by a solid
// footprint at its centre. Clipped: a station whose lane reaches a solid
// footprint keeps the free interval around its centre (`clip`, written by the
// WORLD stage: the corridor stopped there, the footprint kept its DTM, so a
// ribbon over it would be buried or float). Junctions: a side way's station
// inside a dominant's box (profile.js mouthAnchors mark 1) is held on the
// dominant's surface; the side ribbon ends ON the dominant's lane edge (end
// cap vertices on the edge at the dominant's height) and the box is drawn
// once, as the dominant's surface — never two overlapping plates.
//
// The PAINT (S7 DRAW-1, owner O3 / O4; world rev >= PAINT.world_rev): what a
// section of the lane IS — sidewalk strip, ring apron, carriageway — and the
// island kerb tops of doc.rings, read from ways.json (e.sw / e.cw / e.ring,
// doc.rings) by `paintParts`. It is PAINT, never geometry: drawnRibbons'
// pieces / sections / taps are unchanged (every gate tapping them reads the
// same triangles), the split is per-vertex u bounds the client's ribbon
// shader paints (client/src/ways2.js); the only new triangles are the kerb
// tops, a flat strip ON the island the WORLD raised (world/corridor.js
// islandPass: edge + kerb_m), inward from the kerb face world/emit.js
// kerbFaces emits. So DRAW_REV (the clip / junction geometry) stays 1.
'use strict';

// STEP GENERATORS (client-load-unresponsive): drawnRibbons and forkDraw are
// built by step generators (drawnSteps / forkSteps) that `yield` between runs;
// drain() runs one to the end in one go — the synchronous API every caller
// (the WORLD stage, the gates) keeps. The client (client/src/decode.js
// runSteps) spreads the steps over main-thread tasks. A yield never changes
// what is computed, only where the work may pause; the inputs must not change
// while a generator is suspended (nobody writes the doc during the decode).
function drain(it) {
  for (;;) { const r = it.next(); if (r.done) return r.value; }
}

const WAYS_FORMAT = 'vrm-ways';
// 2: + per run `paved` (0/1, the corridor paint's rule) and the sparse
//    per-station lane `clip` [[i, lo, hi] | [i, null, null]] (S4.d)
const WAYS_SCHEMA = 2;
// the drawn geometry's own version (the clip rule, the junction cut): in
// ways.json `draw.rev`; readers of the clip need the rev they know
const DRAW_REV = 1;

// (FACADE-W, rules row facade-width) a run's SURFACE half-extents per
// station. ways.json `fw` (sparse, world/ways.js facadeExtents): [[i, l, r],
// ...] — at surface station i the run's paved surface reaches l to the left
// (-o, o along the perpendicular (-dz, dx) of the run's direction,
// game/solids.js perps) and r to the right, each >= e.w / 2 (the lane stays
// e.w: lane lines, kerb strips and the cross-fall solve keep it); a station
// not listed spans +-e.w / 2. -> null (no fw) or {l, r} Float64Array(n) of
// positive halves. Fails fast on a malformed fw (a reader never guesses).
function surfHalves(e) {
  if (!e.fw) return null;
  const n = e.x.length, h = e.w / 2;
  if (!Array.isArray(e.fw) || e.tw) throw new Error(`ways.json run ${e.path}: fw ${e.tw ? 'beside a bench tread tw' : 'not a list'}`);
  const l = new Float64Array(n).fill(h), r = new Float64Array(n).fill(h);
  for (const c of e.fw) {
    if (!Array.isArray(c) || c.length !== 3 || !Number.isInteger(c[0]) || c[0] < 0 || c[0] >= n
      || !(c[1] >= h - 1e-6) || !(c[2] >= h - 1e-6) || !Number.isFinite(c[1]) || !Number.isFinite(c[2])) {
      throw new Error(`ways.json run ${e.path}: fw entry ${JSON.stringify(c)} (n ${n}, w ${e.w})`);
    }
    l[c[0]] = c[1]; r[c[0]] = c[2];
  }
  return { l, r };
}

// Every reader of ways.json calls checkWays first: an unknown format or
// schema fails fast (a stale reader must never draw / measure a document it
// does not understand).
function checkWays(doc, what = 'ways.json') {
  if (!doc || doc.format !== WAYS_FORMAT || doc.schema !== WAYS_SCHEMA) {
    throw new Error(`${what}: format ${JSON.stringify(doc && doc.format)} schema ${JSON.stringify(doc && doc.schema)}`
      + ` — this reader knows ${WAYS_FORMAT} schema ${WAYS_SCHEMA}`);
  }
  return doc;
}

const DRAW = {
  // footprint clip: lateral taps every <= clip_step_m (the grid cell is 0.5:
  // a tap is blocked when any of the 4 samples of its grid quad is a
  // footprint), cross-sections at the station and 1/4, 1/2 of the way to each
  // neighbour (the part of the ribbon the station's vertices span)
  clip_step_m: 0.25,
  clip_fracs: [0.25, 0.5],
  // a clipped lane narrower than this is not drawn at that station
  min_lane_m: 0.5,
  // junction: the dominant is the way whose surface holds the side station
  // (|side - dominant| after both draw offsets <= dom_dy_m; mouthAnchors
  // measured p1 / p99 -0.15 / +0.14 m on s1), within its box half width
  // (w/2 + max(shoulder, tap margin)) + dom_eps_m
  dom_dy_m: 0.3,
  dom_eps_m: 0.05,
  dom_reach: 4,        // stations either side of a box stretch clipped at the dominant's edge
  dom_window: 8,       // dominant segments searched either side of its nearest
  cut_step_m: 0.25,    // lateral scan of a cross-section against the dominant's lane
  bisect: 16,
  grid_m: 16,          // segment index cell
};

// render class -> [material, texture metres per repeat, view class]; rails:
// the DEFAULT gauge (m) of a run with no recorded one (A TRACK DRAWS AT ITS
// RECORDED GAUGE: run.gauge_m, else its fork group's, forkDraw)
const RENDER = {
  major: { paved: 'major', unpaved: 'path', texLen: 12, view: 'major' },
  minor: { paved: 'minor', unpaved: 'path', texLen: 7, view: 'minor' },
  path: { paved: 'minor', unpaved: 'path', texLen: 7, view: 'path' },
  stairs: { paved: 'stairs', unpaved: 'stairs', texLen: 2, view: 'path' },
  rail: { paved: 'ballast', unpaved: 'ballast', texLen: 4, view: 'rail', rails: 1.435 },
  funicular: { paved: 'ballast', unpaved: 'ballast', texLen: 4, view: 'rail', rails: 1.2 },
};

function renderOf(e) {
  const R = RENDER[e.cls] || RENDER.path;
  let rails = R.rails || 0;
  if (rails && e.gauge_m !== undefined && e.gauge_m !== null) {
    if (!(typeof e.gauge_m === 'number' && e.gauge_m > 0 && e.gauge_m < 3)) throw new Error(`ways.json run (${e.cls}, chain ${e.chain}): gauge_m ${JSON.stringify(e.gauge_m)} is not a track gauge in metres`);
    rails = e.gauge_m;
  }
  return { mat: e.paved ? R.paved : R.unpaved, texLen: R.texLen, view: R.view, rails };
}

// the drawn centre height per station: stairs the TREAD, else the profile.
// When the world publishes `corridor.flight_plane === true` (WAYSG-W), a
// ground flight's ground IS its plane (e.y = the line through the tread
// nosings, cf 0), so the flight draws at e.y like any run: a height taken at
// e.tread would sit up to one riser UNDER the ground. Without the key (bakes
// before it) the tread staircase stands, so old bakes draw unchanged.
function flightPlane(doc) {
  return !!(doc && doc.corridor && doc.corridor.flight_plane === true);
}
// a FLIGHT run: its corridor class is in mode 'flight' (no corridor: pcls steps)
function isFlightRun(doc, e) {
  const C = doc.corridor && doc.corridor.classes && doc.corridor.classes[e.pcls];
  return C ? C.mode === 'flight' : e.pcls === 'steps';
}
// (WAYSG-W, row ground-is-lane) A FLIGHT'S LANE, ONE RULE for the conformed
// ground (world/corridor.js flightLane, plane = params corridor.flight_plane)
// and the draw (baseOf, plane = the published key): plane on, e.y (the
// nosing line) except e.tread at a declared flight's stations (needs
// 'declared': a shaft flight's floor is its treads) and inside a well
// (e.flight.well: the tube's floor) — full11 with the plane there: terrain
// 0.06-0.16 m over a shaft's floor, a drawn ribbon over a raised flight's own
// record; plane off, e.tread; a run without a tread table, e.y.
function flightLaneOf(doc, e, plane) {
  if (!Array.isArray(e.tread) || e.tread.length !== e.x.length) return e.y;
  if (!plane) return e.tread;
  const well = new Set();
  for (const q of (e.flight && e.flight.well) || []) for (let i = q.i[0]; i <= q.i[1]; i++) well.add(i);
  const names = (doc.codes && doc.codes.needs) || [];
  return e.y.map((y, i) => (names[(e.needs || [])[i]] === 'declared' || well.has(i) ? e.tread[i] : y));
}
// the published foot band depth (m); 0 on bakes without it
function flightFoot(doc) {
  const f = doc && doc.corridor && doc.corridor.flight_foot_m;
  return typeof f === 'number' && f > 0 ? f : 0;
}
// (WAYSG-W, foot rule (i), corridor.flight_foot_draw) THE DRAW HOLDS A
// FLIGHT'S FOOT BAND: where the world publishes corridor.flight_foot_m > 0,
// a flight run's `foot` list ([station, y], world/corridor.js flightFootDraw)
// replaces those stations' lane: the corridor's own claim envelope inside
// the foot band (a station within tap_margin_m of arc of a piece end the
// flight rises from, or of a sag where it starts to climb), on PLANE
// stations only (a declared flight / well stands on its treads), never under
// the conformed skin at the station and never over the lane. So the draw,
// the wall clip and the census taps stand on the band where the ground is.
// Without the key or the list the lane is drawn as before.
function baseOf(doc, e) {
  if (!isFlightRun(doc, e)) return e.y;
  const v = flightLaneOf(doc, e, flightPlane(doc));
  if (!(flightFoot(doc) > 0) || !Array.isArray(e.foot) || !e.foot.length) return v;
  const o = Array.from(v);
  for (const [i, y] of e.foot) if (Number.isInteger(i) && i >= 0 && i < o.length && Number.isFinite(y)) o[i] = y;
  return o;
}

// consecutive runs of one chain sharing a boundary station (1 cm)
function chainJoins(doc) {
  const runs = doc.ways;
  const key = (c, x, z) => c + ':' + Math.round(x * 100) + ':' + Math.round(z * 100);
  const starts = new Map();
  runs.forEach((e, r) => { if (e.x.length >= 2) starts.set(key(e.chain, e.x[0], e.z[0]), r); });
  const pred = new Int32Array(runs.length).fill(-1), succ = new Int32Array(runs.length).fill(-1);
  runs.forEach((e, r) => {
    const n = e.x.length;
    if (n < 2) return;
    const q = starts.get(key(e.chain, e.x[n - 1], e.z[n - 1]));
    if (q === undefined || q === r || pred[q] >= 0) return;
    succ[r] = q; pred[q] = r;
  });
  return { pred, succ };
}

// per run the station normals {nx, nz} (+x = +o): game/solids.js perps on the
// run polyline (a < 0.5 m chord repeats the previous normal), the chain's
// neighbouring run supplying the far point at a joined end
function runFrames(doc, joins = chainJoins(doc)) {
  return doc.ways.map((e, r) => {
    const n = e.x.length, nx = new Float64Array(n), nz = new Float64Array(n);
    const P = joins.pred[r] >= 0 ? doc.ways[joins.pred[r]] : null;
    const Q = joins.succ[r] >= 0 ? doc.ways[joins.succ[r]] : null;
    let have = false;
    for (let i = 0; i < n; i++) {
      let ax, az, bx, bz;
      if (i + 1 < n) { bx = e.x[i + 1]; bz = e.z[i + 1]; } else if (Q) { bx = Q.x[1]; bz = Q.z[1]; } else { bx = e.x[i]; bz = e.z[i]; }
      if (i > 0) { ax = e.x[i - 1]; az = e.z[i - 1]; } else if (P) { const m = P.x.length; ax = P.x[m - 2]; az = P.z[m - 2]; } else { ax = e.x[i]; az = e.z[i]; }
      const dx = bx - ax, dz = bz - az, len = Math.hypot(dx, dz);
      if (len < 0.5 && have) { nx[i] = nx[i - 1]; nz[i] = nz[i - 1]; continue; }
      const l = len || 1;
      nx[i] = -dz / l; nz[i] = dx / l;
      have = true;
    }
    return { nx, nz };
  });
}

// ------------------------------------------------------------ footprint clip
// WORLD stage: per SURFACE station the free lateral interval around the
// centre (blocked(x, z) -> true on a tap whose grid quad touches a solid
// footprint). Writes e.clip (sparse, only stations not fully free:
// [i, lo, hi], or [i, null, null] = not drawn) -> counts.
// (FACADE-W) the taps span the run's SURFACE: -l..+r of surfHalves (fw),
// each side its own step <= step_m from the centre tap; a run without fw
// keeps +-e.w / 2 (l = r = h: the same taps, the same bytes). lo / hi may
// then reach past e.w / 2 (drawnSteps clamps to the surface, not the lane).
// (FACADE-W 2) opts.edges (world/ways.js facadeEdges: per run null | {l, r}
// the station's distance to the building OUTLINE per side, NaN none) and
// opts.slack_m: a side whose taps stopped short of min(its surface half, its
// facade) by no more than slack_m is drawn to that line (the taps' grid
// quads and neighbour sections stop up to ~0.8 m before a wall; the outline
// is the facade) -> st.facade_edges; opts.hold(x, z) -> true where the
// ground is held (a pad cell the conform left at its plinth bound, an island
// core): a side whose stretch from its taps' edge to that line, along the
// station's normal every <= step / 2, crosses one keeps the taps' edge
// (st.facade_held).
function laneClip(doc, blocked, opts = {}) {
  const step = opts.step_m || DRAW.clip_step_m;
  const fracs = opts.fracs || DRAW.clip_fracs;
  const edges = opts.edges || null, slack = opts.slack_m || 0, hold = opts.hold || null;
  if (edges && !(slack > 0)) throw new Error('laneClip: opts.edges needs opts.slack_m > 0');
  const F = runFrames(doc);
  const st = { stations: 0, clipped: 0, dropped: 0, runs: 0, facade_edges: 0, facade_held: 0 };
  // a held cell on the station's normal between offsets |a| .. |b| (sign sg)
  const held = (x, z, nx, nz, sg, a, b) => {
    if (!hold) return false;
    const m = Math.max(1, Math.ceil((b - a) / (step / 2)));
    for (let k = 0; k <= m; k++) { const o = sg * (a + ((b - a) * k) / m); if (hold(x + nx * o, z + nz * o)) return true; }
    return false;
  };
  const cm = (v) => Math.round(v * 100) / 100;
  doc.ways.forEach((e, r) => {
    const n = e.x.length, h = e.w / 2, SH = surfHalves(e);
    const taps = (v) => Math.max(1, Math.ceil(v / step));
    let mMax = taps(h);
    if (SH) for (let i = 0; i < n; i++) mMax = Math.max(mMax, taps(SH.l[i]), taps(SH.r[i]));
    const { nx, nz } = F[r];
    const free = new Uint8Array(2 * mMax + 1);
    const out = [];
    const secs = [];
    for (let i = 0; i < n; i++) {
      if (e.flag[i] !== 0) continue;
      st.stations++;
      // L / R: the surface halves left / right; mL / mR taps each side
      const L = SH ? SH.l[i] : h, R = SH ? SH.r[i] : h, mL = taps(L), mR = taps(R);
      secs.length = 0;
      secs.push([e.x[i], e.z[i], nx[i], nz[i]]);
      for (const j of [i - 1, i + 1]) {
        if (j < 0 || j >= n) continue;
        for (const f of fracs) {
          const ax = nx[i] + f * (nx[j] - nx[i]), az = nz[i] + f * (nz[j] - nz[i]);
          const l = Math.hypot(ax, az);
          if (l < 1e-6) continue;
          secs.push([e.x[i] + f * (e.x[j] - e.x[i]), e.z[i] + f * (e.z[j] - e.z[i]), ax / l, az / l]);
        }
      }
      let all = true;
      for (let k = -mL; k <= mR; k++) {
        const o = k < 0 ? (L * k) / mL : (R * k) / mR;
        let ok = 1;
        for (const s of secs) if (blocked(s[0] + s[2] * o, s[1] + s[3] * o)) { ok = 0; break; }
        free[k + mL] = ok;
        if (!ok) all = false;
      }
      if (all) continue;
      if (!free[mL]) { out.push([i, null, null]); st.dropped++; continue; }
      let a = mL, b = mL;
      while (a > 0 && free[a - 1]) a--;
      while (b < mL + mR && free[b + 1]) b++;
      // conservative to the cm: never widen into the blocked tap
      let lo = Math.ceil(((L * (a - mL)) / mL) * 100) / 100, hi = Math.floor(((R * (b - mL)) / mR) * 100) / 100;
      const E = edges && edges[r];
      if (E) {
        const tl = Math.floor(Math.min(L, E.l[i]) * 100 + 1e-6) / 100, tr = Math.floor(Math.min(R, E.r[i]) * 100 + 1e-6) / 100;
        if (tl === tl && -lo < tl && -lo >= tl - slack) {
          if (held(e.x[i], e.z[i], nx[i], nz[i], -1, -lo, tl)) st.facade_held++; else { lo = -tl; st.facade_edges++; }
        }
        if (tr === tr && hi < tr && hi >= tr - slack) {
          if (held(e.x[i], e.z[i], nx[i], nz[i], 1, hi, tr)) st.facade_held++; else { hi = tr; st.facade_edges++; }
        }
      }
      if (hi - lo < DRAW.min_lane_m) { out.push([i, null, null]); st.dropped++; continue; }
      out.push([i, cm(lo), cm(hi)]);
      st.clipped++;
    }
    if (out.length) { e.clip = out; st.runs++; } else delete e.clip;
  });
  return st;
}

// ------------------------------------------------------------ segment index
function segIndex(doc, R, ok) { return drain(segIndexSteps(doc, R, ok)); }
function* segIndexSteps(doc, R, ok) {
  const B = DRAW.grid_m, grid = new Map();
  const key = (bx, bz) => (bx + 4096) * 16384 + (bz + 4096);
  // de-dup stamps per segment (no Set per query)
  const off = new Int32Array(doc.ways.length + 1);
  doc.ways.forEach((e, r) => { off[r + 1] = off[r] + Math.max(0, e.x.length - 1); });
  const stamp = new Uint32Array(off[doc.ways.length]);
  let tick = 0;
  for (let r = 0; r < doc.ways.length; r++) {
    const e = doc.ways[r];
    if ((r & 255) === 255) yield;
    for (let j = 0; j + 1 < e.x.length; j++) {
      if (!ok[r][j] || !ok[r][j + 1]) continue;
      const bx0 = Math.floor(Math.min(e.x[j], e.x[j + 1]) / B), bx1 = Math.floor(Math.max(e.x[j], e.x[j + 1]) / B);
      const bz0 = Math.floor(Math.min(e.z[j], e.z[j + 1]) / B), bz1 = Math.floor(Math.max(e.z[j], e.z[j + 1]) / B);
      for (let bx = bx0; bx <= bx1; bx++) {
        for (let bz = bz0; bz <= bz1; bz++) {
          const k = key(bx, bz);
          let a = grid.get(k);
          if (!a) grid.set(k, a = []);
          a.push(r, j);
        }
      }
    }
  }
  return {
    // every indexed segment (run, j) whose cell lies within rad of (x, z)
    each(x, z, rad, fn) {
      const bx0 = Math.floor((x - rad) / B), bx1 = Math.floor((x + rad) / B);
      const bz0 = Math.floor((z - rad) / B), bz1 = Math.floor((z + rad) / B);
      if (++tick === 0xffffffff) { stamp.fill(0); tick = 1; }
      for (let bx = bx0; bx <= bx1; bx++) {
        for (let bz = bz0; bz <= bz1; bz++) {
          const a = grid.get(key(bx, bz));
          if (!a) continue;
          for (let t = 0; t < a.length; t += 2) {
            const id = off[a[t]] + a[t + 1];
            if (stamp[id] === tick) continue;
            stamp[id] = tick;
            fn(a[t], a[t + 1]);
          }
        }
      }
    },
  };
}

// (x, z) against segment j of run e: {d (to the centreline), f, o (signed
// along the segment normal), v (the drawn surface there)}
function segAt(e, base, j, x, z) {
  const ax = e.x[j], az = e.z[j], dx = e.x[j + 1] - ax, dz = e.z[j + 1] - az;
  const L2 = dx * dx + dz * dz;
  const rx = x - ax, rz = z - az;
  const f = L2 > 1e-12 ? Math.max(0, Math.min(1, (rx * dx + rz * dz) / L2)) : 0;
  const L = Math.sqrt(L2) || 1;
  const o = (rx * -dz + rz * dx) / L;
  const d = Math.hypot(rx - f * dx, rz - f * dz);
  const cf = e.cf[j] + f * (e.cf[j + 1] - e.cf[j]);
  return { d, f, o, v: base[j] + f * (base[j + 1] - base[j]) + cf * o };
}

// the distance alone (segAt's d, no allocation: the hot loops)
function segD(e, j, x, z) {
  const ax = e.x[j], az = e.z[j], dx = e.x[j + 1] - ax, dz = e.z[j + 1] - az;
  const L2 = dx * dx + dz * dz, rx = x - ax, rz = z - az;
  const f = L2 > 1e-12 ? Math.max(0, Math.min(1, (rx * dx + rz * dz) / L2)) : 0;
  return Math.hypot(rx - f * dx, rz - f * dz);
}

// ------------------------------------------------------------ ground paths
// PATH-G (CONTRACT C1, AMENDMENT 1 C1'): an unpaved footpath / alpine path /
// track run is not a strip mesh; it is part of the ONE terrain surface (the
// WORLD path field world/pathfield.mjs, baked into the texture tiles, read by
// the ground shader). The ONE predicate.
function isGroundPath(e) {
  return e.cls === 'path' && !e.paved && (e.pcls === 'path' || e.pcls === 'alpine' || e.pcls === 'track');
}

// THE LOOK KIND OF A RUN (one rule: world/pathfield.mjs kindOf for the ground,
// world/emit.js emitDecks for a deck's top, row deck-top-is-its-way). look =
// pipeline/wayslook.json. surfaceKind: the wayslook built kind listing an OSM
// surface value, else null.
const LOOK_WAY_KINDS = ['asphalt', 'concrete', 'paving', 'sett', 'gravel', 'ballast', 'steps'];
const LOOK_GRADE = { grade1: 1, grade2: 2, grade3: 3, grade4: 4, grade5: 5 };
function surfaceKind(look, s) {
  if (typeof s !== 'string') return null;
  for (const k of LOOK_WAY_KINDS) if (look.surfaces[k] && look.surfaces[k].includes(s)) return k;
  return null;
}
// run e -> {kind, hi, side} (look.kinds ids; side: the sidewalk kind id of a
// road kind). tags: the run's OSM way tags (the WORLD intake) or null
function wayKind(e, look, tags = null) {
  const K = look.kinds;
  if (e.cls === 'funicular') return { kind: K.none, hi: 0, side: 0 };
  if (e.cls === 'stairs' || e.pcls === 'steps') return { kind: K.steps, hi: 0, side: 0 };
  if (e.cls === 'rail') return { kind: K.ballast, hi: 0, side: 0 };
  if (isGroundPath(e)) {
    if (e.pcls === 'track') return { kind: K.track, hi: (e.tt && LOOK_GRADE[e.tt.tracktype]) || 0, side: 0 };
    const s = e.tt && typeof e.tt.surface === 'string' ? e.tt.surface : null;
    const sac = Number.isInteger(e.sac) && e.sac >= 1 && e.sac <= 6 ? e.sac : 0;
    const rock = (s !== null && look.rock_surfaces.includes(s)) || sac >= look.rock_sac;
    return { kind: rock ? K.rock : K.soil, hi: sac, side: 0 };
  }
  const k = surfaceKind(look, e.tt && e.tt.surface) || (e.paved ? look.paved_default : look.unpaved_default);
  let sw = null;
  if (tags) for (const t of look.sidewalk.tags) { sw = surfaceKind(look, tags[t]); if (sw) break; }
  return { kind: K[k], hi: 0, side: K[sw || look.sidewalk.kind] };
}

// WAYS-G (waysg CONTRACT C1, AMENDMENT 1 A1): EVERY way on a SURFACE
// station is drawn by the ground — road, rail, funicular, track, path and
// stairs alike; no ground strip of any class remains. Deck (kind 1) and bore
// stations are never drawnRibbons pieces (deckRibbons / structures own
// them), raised flights over air are STR1 structures, platforms structures.
// A superset of isGroundPath. The ONE predicate (W2 reads it).
function isGroundWay(e) {
  return !!e;
}
// a way whose surface never holds another's junction box under groundWays:
// stairs (their treads would run across the road they end on) and the
// funicular (A4: no surface kind — the ground keeps its own class there)
function weakBox(e) {
  return e.cls === 'stairs' || e.pcls === 'steps' || e.cls === 'funicular';
}

// ------------------------------------------------------------ the pieces
// -> {pieces: [{run, cls, mat, view, texLen, rails, w, sec: [section...]}],
//     stats}; a section is {i (station), s (arc m), lo, hi, a: [x,y,z] (lo
//     vertex), b: [x,y,z] (hi vertex), cap (1: a junction end on the
//     dominant's edge)}
// opts.groundPaths (PATH-G, CONTRACT C2; default false = today's output byte
// for byte): every piece of a ground-path run carries `ground: 1`, and a
// ground path is never the dominant of a junction box over a non-ground way
// (the box stays the drawn way's surface); between two ground paths today's
// rule stands.
// opts.groundWays (WAYS-G, waysg CONTRACT C1 / AMENDMENT 1; supersedes
// groundPaths): every piece carries `ground: 1` (isGroundWay). The field
// draws the union, a box is its dominant's surface (today's rule), except
// that a weaker run never dominates the box of a stronger one — weakBox
// (stairs, funicular) < ground path (isGroundPath, PATH-G C2) < built way:
// the road keeps its surface, the stairs / the track end at its edge.
// No way loses surface where another meets it; the default (no option)
// stays byte-identical.
function drawnRibbons(doc, opts = {}) { return drain(drawnSteps(doc, opts)); }
function* drawnSteps(doc, opts = {}) {
  const runs = doc.ways;
  const GW = opts.groundWays ? runs.map(isGroundWay) : null;
  // the box rank under groundWays (review F2): a weakBox run (0) never
  // dominates a ground path's / built way's box, a ground path (1, PATH-G C2)
  // never a built way's (2); between equal ranks today's rule stands
  const WK = GW ? runs.map((e) => (weakBox(e) ? 0 : isGroundPath(e) ? 1 : 2)) : null;
  const GP = GW || (opts.groundPaths ? runs.map(isGroundPath) : null);
  const joins = chainJoins(doc);
  yield;
  const F = runFrames(doc, joins);
  yield;
  const tap = (doc.corridor && doc.corridor.rules && doc.corridor.rules.tap_margin_m) || 0.71;
  const shoulderOf = (e) => {
    const c = doc.corridor && doc.corridor.classes && doc.corridor.classes[e.pcls];
    return c ? c.shoulder_m || 0 : 0;
  };
  const bases = runs.map((e) => baseOf(doc, e));
  yield;
  // (FACADE-W) per run the SURFACE halves (surfHalves: fw, null without):
  // the extension is part of the run's drawn surface wherever overlap /
  // dominance asks how far a run's ribbon reaches; the lane (e.w / 2) stays
  // the lane wherever a lane claim is asked (walled)
  const SH = runs.map(surfHalves);
  // run q's surface half on the side of signed offset o at segment j,
  // fraction f (segAt's f / o); e.w / 2 without fw
  const surfAt = (q, j, f, o) => {
    const S = SH[q];
    if (!S) return runs[q].w / 2;
    const A = o < 0 ? S.l : S.r;
    return A[j] + f * (A[j + 1] - A[j]);
  };
  // per station: the free interval (footprint clip) or null (not drawn)
  const LO = [], HI = [], ok = [];
  runs.forEach((e, r) => {
    // (t28 bench) a bench station's tread (ways.json tw) narrows its lane;
    // (FACADE-W) an fw station spans -l..+r (a run never has both)
    const n = e.x.length, hw = (i) => (e.tw ? e.tw[i] : e.w) / 2, S = SH[r];
    const hl = S ? (i) => S.l[i] : hw, hr = S ? (i) => S.r[i] : hw;
    const lo = new Float64Array(n), hi = new Float64Array(n), k = new Uint8Array(n);
    for (let i = 0; i < n; i++) { k[i] = e.flag[i] === 0 ? 1 : 0; lo[i] = -hl(i); hi[i] = hr(i); }
    for (const c of e.clip || []) {
      const i = c[0];
      if (!(i >= 0 && i < n)) continue;
      if (c[1] === null || c[2] === null) { k[i] = 0; continue; }
      lo[i] = Math.max(-hl(i), c[1]); hi[i] = Math.min(hr(i), c[2]);
    }
    LO.push(lo); HI.push(hi); ok.push(k);
  });
  yield;
  const idx = yield* segIndexSteps(doc, null, ok);
  yield;
  const stats = { box_stations: 0, box_dominant: 0, box_no_dominant: 0, box_inside: 0, caps: 0, dom_clipped: 0, wall_clipped: 0, wall_hidden: 0 };
  const kerb = (doc.corridor && doc.corridor.rules && doc.corridor.rules.kerb_m) || 0.3;
  // (FACADE-W: the lane) the widest other LANE a wall candidate can hold:
  // walled tests the other run's lane claim (Q.w / 2), never its fw surface
  const maxHalf = runs.reduce((a, e) => Math.max(a, e.w / 2), 0);

  // ---- the dominant of every box station
  const dom = runs.map((e) => null);
  const domRun = (e, r) => {
    const M = e.mouth;
    if (!M) return;
    for (let i = 0; i < e.x.length; i++) {
      if (M[i] !== 1 || !ok[r][i]) continue;
      stats.box_stations++;
      const x = e.x[i], z = e.z[i], ys = bases[r][i] - e.lift;
      // a candidate held in a box itself (mouth 1 at the segment) is another
      // side: only when no through way qualifies
      // every qualifying run of the best rank is part of the box's dominant
      // (two through runs meeting at the box: one way to the eye)
      const cand = new Map();   // q -> {q, j, dy, k}
      idx.each(x, z, 12, (q, j) => {
        // never the run itself nor its chain neighbour (the same way, joined)
        if (q === r || q === joins.pred[r] || q === joins.succ[r]) return;
        // (PATH-G C2) a ground path never dominates a drawn way's box
        if (WK) { if (WK[q] < WK[r]) return; }
        else if (GP && GP[q] && !GP[r]) return;
        const Q = runs[q];
        const p = segAt(Q, bases[q], j, x, z);
        // (FACADE-W: the surface) the box reaches Q's drawn surface on the
        // station's side (its fw extension included), + shoulder / tap
        if (p.d > surfAt(q, j, p.f, p.o) + Math.max(shoulderOf(Q), tap) + DRAW.dom_eps_m) return;
        const dy = Math.abs(ys - (p.v - Q.lift));
        if (dy > DRAW.dom_dy_m) return;
        const k = Q.mouth && (Q.mouth[j] === 1 || Q.mouth[j + 1] === 1) ? 1 : 0;
        const c = cand.get(q);
        if (!c || k < c.k || (k === c.k && dy < c.dy)) cand.set(q, { q, j, dy, k });
      });
      let best = null, bd = Infinity;
      const kmin = Math.min(2, ...[...cand.values()].map((c) => c.k));
      const all = [...cand.values()].filter((c) => c.k === kmin);
      for (const c of all) if (c.dy < bd) { bd = c.dy; best = { q: c.q, j: c.j }; }
      if (best) best.alt = all.filter((c) => c.q !== best.q).map((c) => ({ q: c.q, j: c.j }));
      if (best && bd <= DRAW.dom_dy_m) {
        if (!dom[r]) dom[r] = new Array(e.x.length).fill(null);
        dom[r][i] = best;
        stats.box_dominant++;
      } else stats.box_no_dominant++;
    }
  };
  for (let r = 0; r < runs.length; r++) { domRun(runs[r], r); yield; }

  // g < 0: (x, z) inside the dominant's drawn lane; +1 where its ribbon is not drawn
  // (FACADE-W: the surface) an fw dominant's lane is its drawn surface,
  // per side: a side way stops at the dominant's extension edge
  // (the dominant way continues through its chain joins: the neighbour's
  // end window counts as the dominant too; so do the box's other through
  // runs of the same rank, D.alt)
  const inside = (D, x, z) => {
    let bg = Infinity, bq = -1, bj = -1;
    const scan = (q, j0, j1) => {
      const Q = runs[q], hw = Q.w / 2, wide = !!SH[q];
      j0 = Math.max(0, j0); j1 = Math.min(Q.x.length - 2, j1);
      for (let j = j0; j <= j1; j++) {
        let g;
        if (wide) { const p = segAt(Q, bases[q], j, x, z); g = p.d - surfAt(q, j, p.f, p.o); } else g = segD(Q, j, x, z) - hw;
        if (g < bg) { bg = g; bq = q; bj = j; }
      }
    };
    const W = DRAW.dom_window;
    const one = (A) => {
      const nQ = runs[A.q].x.length;
      scan(A.q, A.j - W, A.j + W);
      if (A.j - W < 0 && joins.pred[A.q] >= 0) { const P = joins.pred[A.q], m = runs[P].x.length; scan(P, m - 1 - (W - A.j), m - 2); }
      if (A.j + W > nQ - 2 && joins.succ[A.q] >= 0) scan(joins.succ[A.q], 0, A.j + W - (nQ - 2));
    };
    one(D);
    if (D.alt) for (const A of D.alt) one(A);
    if (bq < 0 || !ok[bq][bj] || !ok[bq][bj + 1]) return { g: 1, v: NaN };
    return { g: bg, v: segAt(runs[bq], bases[bq], bj, x, z).v };
  };

  const pieces = [];
  const om = {};   // omitted metres by reason, per render class
  const rowOf = opts.rowOf || ((e) => e.cls);
  const addOm = (e, why, m) => {
    const cls = rowOf(e);
    const o = om[cls] || (om[cls] = { deck_m: 0, bore_m: 0, drop_m: 0, footprint_m: 0, junction_m: 0, wall_m: 0, drawn_m: 0 });
    o[why] += m;
  };
  const pieceRun = (e, r) => {
    const n = e.x.length;
    if (n < 2) return;
    const { nx, nz } = F[r];
    const base = bases[r], h = e.w / 2;
    // (FACADE-W: the surface) the scans' reach per side: -l / +r (h without fw)
    const sL = SH[r] ? SH[r].l : null, sR = SH[r] ? SH[r].r : null;
    const reach = (i, sgn) => (sgn < 0 ? (sL ? sL[i] : h) : (sR ? sR[i] : h));
    const RD = renderOf(e);
    const s = new Float64Array(n);
    for (let i = 1; i < n; i++) s[i] = s[i - 1] + Math.hypot(e.x[i] - e.x[i - 1], e.z[i] - e.z[i - 1]);
    // the dominant each station is clipped against (box stations + reach)
    const Ds = new Array(n).fill(null);
    if (dom[r]) {
      for (let i = 0; i < n; i++) {
        if (!dom[r][i]) continue;
        for (let k = Math.max(0, i - DRAW.dom_reach); k <= Math.min(n - 1, i + DRAW.dom_reach); k++) {
          if (!Ds[k] || Math.abs(k - i) < Ds[k].k) Ds[k] = { ...dom[r][i], k: Math.abs(k - i) };
        }
      }
    }
    // state per station: 0 not drawn, 1 drawn, 2 inside the dominant's lane
    const state = new Uint8Array(n), lo = Float64Array.from(LO[r]), hi = Float64Array.from(HI[r]);
    const loD = new Array(n).fill(null), hiD = new Array(n).fill(null), walls = new Uint8Array(n);
    const pt = (i, o) => [e.x[i] + nx[i] * o, e.z[i] + nz[i] * o];
    for (let i = 0; i < n; i++) {
      if (!ok[r][i]) continue;
      state[i] = 1;
      const D = Ds[i];
      if (!D) continue;
      const P0 = pt(i, 0);
      if (inside(D, P0[0], P0[1]).g < 0) { state[i] = 2; if (dom[r] && dom[r][i]) stats.box_inside++; continue; }
      // scan each side outward from the centre; the first inside tap bounds it
      // (FACADE-W: the surface) out to the side's reach: a higher-rank
      // dominant cuts an fw run's extension too
      for (const sgn of [-1, 1]) {
        const lim = sgn < 0 ? lo[i] : hi[i], E = reach(i, sgn), m = Math.max(1, Math.ceil(E / DRAW.cut_step_m));
        let prev = 0, hit = null;
        for (let k = 1; k <= m; k++) {
          let o = (sgn * E * k) / m;
          if (sgn < 0 ? o < lim : o > lim) o = lim;
          const P = pt(i, o);
          if (inside(D, P[0], P[1]).g < 0) { hit = o; break; }
          prev = o;
          if (o === lim) break;
        }
        if (hit === null) continue;
        let a = prev, b = hit;
        for (let t = 0; t < DRAW.bisect; t++) {
          const c = (a + b) / 2, P = pt(i, c);
          if (inside(D, P[0], P[1]).g < 0) b = c; else a = c;
        }
        if (sgn < 0) { lo[i] = a; loD[i] = D; } else { hi[i] = a; hiD[i] = D; }
        stats.dom_clipped++;
      }
      if (hi[i] - lo[i] < DRAW.min_lane_m / 4) state[i] = 2;
    }
    // lane walls (corridor.js: two lanes more than kerb_m apart, the cell
    // is the claim it lies deepest inside, |o| - w/2): the part of this lane
    // another run's lane owns is that lane's ground, not this ribbon's.
    // Stations clipped at a box dominant are not re-clipped.
    // (FACADE-W: the lane) both claims stay lanes (er |o| - h, eq Q.w / 2,
    // corridor.js' claim rule): on this run's extension er > 0, so another
    // lane beyond a kerb step always walls it — an fw run never claims
    // another run's lane; the scan alone reaches the wide side
    const own = (i, o) => base[i] - e.lift + e.cf[i] * o;
    // the other lanes that can reach station i's section (gathered once)
    let cand = [];
    const walled = (i, o) => {
      const P = pt(i, o), er = Math.abs(o) - h, yo = own(i, o);
      for (let c = 0; c < cand.length; c += 2) {
        const q = cand[c], Q = runs[q];
        const eq = segD(Q, cand[c + 1], P[0], P[1]) - Q.w / 2;
        if (eq >= 0 || eq >= er) continue;
        const p = segAt(Q, bases[q], cand[c + 1], P[0], P[1]);
        if (Math.abs(yo - (p.v - Q.lift)) > kerb) return true;
      }
      return false;
    };
    for (let i = 0; i < n; i++) {
      if (state[i] !== 1 || Ds[i]) continue;
      cand = [];
      const xi = e.x[i], zi = e.z[i];
      // (FACADE-W: the surface) candidates within this station's wider side
      const hs = Math.max(reach(i, -1), reach(i, 1));
      idx.each(xi, zi, hs + maxHalf, (q, j) => {
        if (q === r || q === joins.pred[r] || q === joins.succ[r]) return;
        const Q = runs[q], lim = hs + Q.w / 2;
        const ax = Q.x[j], bx = Q.x[j + 1], az = Q.z[j], bz = Q.z[j + 1];
        if (xi < Math.min(ax, bx) - lim || xi > Math.max(ax, bx) + lim || zi < Math.min(az, bz) - lim || zi > Math.max(az, bz) + lim) return;
        if (segD(Q, j, xi, zi) < lim) cand.push(q, j);
      });
      if (!cand.length) continue;
      if (walled(i, 0)) { state[i] = 0; walls[i] = 1; stats.wall_hidden++; continue; }
      for (const sgn of [-1, 1]) {
        const lim = sgn < 0 ? lo[i] : hi[i], E = reach(i, sgn), m = Math.max(1, Math.ceil(E / DRAW.cut_step_m));
        let prev = 0, hit = null;
        for (let k = 1; k <= m; k++) {
          let o = (sgn * E * k) / m;
          if (sgn < 0 ? o < lim : o > lim) o = lim;
          if (walled(i, o)) { hit = o; break; }
          prev = o;
          if (o === lim) break;
        }
        if (hit === null) continue;
        let a = prev, b = hit;
        for (let t = 0; t < DRAW.bisect; t++) {
          const c = (a + b) / 2;
          if (walled(i, c)) b = c; else a = c;
        }
        if (sgn < 0) lo[i] = a; else hi[i] = a;
        stats.wall_clipped++;
      }
      if (hi[i] - lo[i] < DRAW.min_lane_m) { state[i] = 0; walls[i] = 1; stats.wall_hidden++; }
    }
    // vertex of station i at offset o: the dominant's surface on its edge
    const vtx = (i, o, D) => {
      const P = pt(i, o);
      let y = base[i] + e.cf[i] * o;
      if (D) { const g = inside(D, P[0], P[1]); if (Number.isFinite(g.v)) y = g.v; }
      return [P[0], y, P[1]];
    };
    const section = (i) => ({ i, s: s[i], lo: lo[i], hi: hi[i], a: vtx(i, lo[i], loD[i]), b: vtx(i, hi[i], hiD[i]), cap: 0 });
    // the end cap between drawn station i and inside station j: each edge
    // line (the offset held) runs to the dominant's lane edge
    const cap = (i, j) => {
      const D = Ds[j] || Ds[i];
      const edge = (o, onD) => {
        const A = pt(i, o), B = [e.x[j] + nx[j] * o, e.z[j] + nz[j] * o];
        const at = (f) => [A[0] + f * (B[0] - A[0]), A[1] + f * (B[1] - A[1])];
        let f = 1;
        if (onD) f = 0;
        else if (inside(D, B[0], B[1]).g < 0) {
          let a = 0, b = 1;
          for (let t = 0; t < DRAW.bisect; t++) {
            const c = (a + b) / 2, P = at(c);
            if (inside(D, P[0], P[1]).g < 0) b = c; else a = c;
          }
          f = a;
        }
        // f == 0: the cap coincides with station i's own section -> its vertex
        if (f <= 0) return { p: vtx(i, o, onD ? D : null), f };
        const P = at(f), g = inside(D, P[0], P[1]);
        const own = base[i] + f * (base[j] - base[i]) + (e.cf[i] + f * (e.cf[j] - e.cf[i])) * o;
        return { p: [P[0], f < 1 && Number.isFinite(g.v) ? g.v : own, P[1]], f };
      };
      const A = edge(lo[i], !!loD[i]), B = edge(hi[i], !!hiD[i]);
      stats.caps++;
      const f = (A.f + B.f) / 2;
      return { i, s: s[i] + f * (s[j] - s[i]), lo: lo[i], hi: hi[i], a: A.p, b: B.p, cap: 1 };
    };
    let cur = null;
    const flush = () => {
      if (cur && cur.length >= 2) {
        const pc = { run: r, cls: e.cls, mat: RD.mat, view: RD.view, texLen: RD.texLen, rails: RD.rails, w: e.w, sec: cur };
        if (GP && GP[r]) pc.ground = 1;
        pieces.push(pc);
      }
      cur = null;
    };
    for (let i = 0; i < n; i++) {
      if (state[i] === 1) {
        if (!cur) { cur = []; if (i > 0 && state[i - 1] === 2) cur.push(cap(i, i - 1)); }
        cur.push(section(i));
        if (i + 1 < n && state[i + 1] === 2) { cur.push(cap(i, i + 1)); flush(); }
      } else flush();
    }
    flush();
    // metres per gap by what happened to it
    for (let i = 0; i + 1 < n; i++) {
      const m = s[i + 1] - s[i];
      const k = Math.max(e.kind[i], e.kind[i + 1]);
      if (state[i] === 1 && state[i + 1] === 1) addOm(e, 'drawn_m', m);
      else if (e.flag[i] || e.flag[i + 1]) addOm(e, k === 1 ? 'deck_m' : k === 2 ? 'bore_m' : k === 3 ? 'drop_m' : 'deck_m', m);
      else if (state[i] === 2 || state[i + 1] === 2) addOm(e, 'junction_m', m);
      else if (walls[i] || walls[i + 1]) addOm(e, 'wall_m', m);
      else addOm(e, 'footprint_m', m);
    }
  };
  for (let r = 0; r < runs.length; r++) { pieceRun(runs[r], r); yield; }
  for (const o of Object.values(om)) for (const k of Object.keys(o)) o[k] = Math.round(o[k] * 10) / 10;
  stats.metres = om;
  stats.pieces = pieces.length;
  return { pieces, stats, frames: F, bases, ok, dom };
}

// ------------------------------------------------------------ the paint
// S7 DRAW-1 (owner O3 "check about sidewalks", O4 "a roundabout in
// reality"): A SIDEWALK IS DRAWN AS ITS OWN STRIP, A ROUNDABOUT IS DRAWN AS A
// RING. Constants of the paint; colours are sRGB hex (the client converts).
const PAINT = {
  world_rev: 17,          // the first world rev whose ways.json carries doc.rings / e.sw / e.cw (S6.d B4c-1): from it on a missing field fails fast
  kerb_top_m: 0.2,        // drawn width of the island kerb stone's top, inward from the kerb face (the island core's edge)
  kerb_line_m: 0.15,      // the sidewalk's kerb stone line along the carriageway side of the strip
  slab_m: 1.0,            // sidewalk slab joint spacing, along and across (m)
  joint_m: 0.04,          // sidewalk slab joint width (m)
  sett_m: 0.5,            // ring apron sett (paving block) size (m)
  sett_joint_m: 0.05,     // ring apron sett joint width (m)
  w_unit_m: 0.25,         // the client's u8 width attribute unit (w <= 63.75 m)
  tri_add_max: 0.02,      // the paint's new triangles (the kerb tops) over the ribbon's, at most (scripts/check-draw.mjs region row)
  colour: {
    sidewalk: '#a7a39b',  // concrete paving slabs
    joint: '#85817a',     // slab joints
    kerb: '#6f7275',      // kerb stone, dark granite: darker than line paint, cooler than slabs (sidewalk kerb line, island kerb top)
    apron: '#8e7f6c',     // ring apron setts
    sett: '#74685a',      // sett joints
    line: '#d8d8d2',      // lane lines (MARKS)
  },
};

// the rings of a doc that carries the paint fields, or null (an older world:
// no paint). Fails fast where a world that claims them misses or garbles one.
function paintDoc(doc, worldRev) {
  const has = Array.isArray(doc.rings);
  if (!has) {
    if (Number.isFinite(worldRev) && worldRev >= PAINT.world_rev) {
      throw new Error(`ways.json of world rev ${worldRev} carries no rings (>= ${PAINT.world_rev}: rings / sw / cw expected, S6.d B4c-1)`);
    }
    for (const e of doc.ways) if (e.sw || e.ring != null) throw new Error('ways.json: a run carries sw / ring but the doc has no rings');
    return null;
  }
  const num = (v) => typeof v === 'number' && Number.isFinite(v);
  doc.rings.forEach((g, k) => {
    const bad = (f) => { throw new Error(`ways.json rings[${k}] (way ${g && g.way}): ${f}`); };
    if (!g || !Number.isInteger(g.lanes) || g.lanes < 1) bad('lanes');
    if (!(num(g.cw) && g.cw > 0) || !(num(g.apron) && g.apron >= 0) || !(num(g.w) && g.w > 0)) bad('cw / apron / w');
    if (!(num(g.kerb_m) && g.kerb_m >= 0)) bad('kerb_m');
    if (typeof g.oneway !== 'boolean') bad('oneway');
    if (!Array.isArray(g.runs)) bad('runs');
    if (g.island !== null) {
      if (!Array.isArray(g.island) || g.island.length < 3 || !g.island.every((p) => Array.isArray(p) && p.length === 3 && p.every(num))) bad('island');
      if (!Array.isArray(g.centre) || !g.centre.every(num)) bad('centre');
    } else if (typeof g.why !== 'string') bad('island null without why');
  });
  if (doc.rings.some((g) => g.island) && !num(doc.corridor && doc.corridor.rules && doc.corridor.rules.tap_margin_m)) {
    throw new Error('ways.json: rings with islands but no corridor.rules.tap_margin_m (the kerb face line)');
  }
  doc.ways.forEach((e, r) => {
    const bad = (f) => { throw new Error(`ways.json ways[${r}]: ${f}`); };
    if (e.ring != null && !(Number.isInteger(e.ring) && e.ring >= 0 && e.ring < doc.rings.length)) bad(`ring ${e.ring}`);
    if (e.sw === undefined) return;
    if (!Array.isArray(e.sw) || e.sw.length !== e.x.length) bad('sw is not one [left, right] per station');
    for (const p of e.sw) if (!Array.isArray(p) || p.length !== 2 || !num(p[0]) || !num(p[1]) || p[0] < 0 || p[1] < 0 || p[0] + p[1] > e.w + 1e-6) bad(`sw ${JSON.stringify(p)} (w ${e.w})`);
    if (!(num(e.cw) && e.cw > 0)) bad('sw without cw');
  });
  return doc.rings;
}

// a closed polygon [[x, z, ...]] offset inward by h (mitred, the mitre
// denominator clamped at 0.2) — world/corridor.js islandCore's formula, kept
// equal by scripts/check-draw.mjs -> {cx, cz}
function insetPoly(pts, h) {
  const N = pts.length;
  let A2 = 0;
  for (let i = 0; i < N; i++) { const p = pts[i], q = pts[(i + 1) % N]; A2 += p[0] * q[1] - q[0] * p[1]; }
  const sgn = A2 > 0 ? 1 : -1;
  const nrm = (i) => { const p = pts[i], q = pts[(i + 1) % N]; const L = Math.hypot(q[0] - p[0], q[1] - p[1]) || 1; return [sgn * -(q[1] - p[1]) / L, sgn * (q[0] - p[0]) / L]; };
  const cx = [], cz = [];
  for (let i = 0; i < N; i++) {
    const n1 = nrm((i - 1 + N) % N), n2 = nrm(i), d = Math.max(0.2, 1 + n1[0] * n2[0] + n1[1] * n2[1]);
    cx.push(pts[i][0] + h * (n1[0] + n2[0]) / d); cz.push(pts[i][1] + h * (n1[1] + n2[1]) / d);
  }
  return { cx, cz };
}

// the island edge height at (x, z): inverse-square weights over the island
// vertices — world/corridor.js islandEdgeY (the kerb face's foot line)
function islandEdgeY(isl, x, z) {
  let sw = 0, sy = 0;
  for (const p of isl) {
    const d2 = (p[0] - x) ** 2 + (p[1] - z) ** 2;
    if (d2 < 1e-9) return p[2];
    sw += 1 / d2; sy += p[2] / d2;
  }
  return sy / sw;
}

// paintParts(doc, {pieces, frames} = drawnRibbons(doc), {worldRev}) ->
//   null (a world before PAINT.world_rev: no paint) or
//   { parts: per piece {u: Float32Array(4 per section) [sidewalk-left end,
//       carriageway start, carriageway end, sidewalk-right start] in the
//       ribbon's u (0 = o -w/2, 1 = o +w/2; identity [0, 0, 1, 1]), lanes
//       (a one-way ring's lane count, 0 = two-way; the lines are laneMarks'),
//       plain (every section identity, no ring)},
//     kerbs: [{ring, run, cls, mat, view, texLen, rails 0, w, kerb: 1, sec}]
//       (piece-shaped: the island kerb top, one closed strip per island),
//     stats }
// Per station i of run e: the sidewalk strips e.sw[i] [left, right] sit at
// the formation's edges (left = -o, the run's left); a ring run's apron
// (doc.rings[e.ring].apron) lies next to the island-side strip, the side
// the ring's centre is on; the carriageway is the rest.
function paintParts(doc, drawn, opts = {}) { return drain(paintSteps(doc, drawn, opts)); }
function* paintSteps(doc, drawn, opts = {}) {
  const rings = paintDoc(doc, opts.worldRev);
  if (!rings) return null;
  const runs = doc.ways, F = drawn.frames;
  const stats = { pieces: drawn.pieces.length, sidewalk_pieces: 0, sidewalk_sections: 0, sidewalk_runs: 0, ring_pieces: 0, ring_sections: 0,
    apron_sections: 0, ring_no_centre_sections: 0, oneway_ring_pieces: 0, kerbs: 0, kerb_tris: 0, ribbon_tris: 0, sidewalk_m2: 0 };
  const swRuns = new Set();
  const partOf = (p) => {
    const e = runs[p.run], w = e.w, n = p.sec.length, u = new Float32Array(4 * n);
    const g = e.ring != null ? rings[e.ring] : null;
    const { nx, nz } = F[p.run];
    let plain = !g, sw = false;
    stats.ribbon_tris += 2 * (n - 1);
    for (let k = 0; k < n; k++) {
      const i = p.sec[k].i;
      const l = e.sw ? e.sw[i][0] : 0, r = e.sw ? e.sw[i][1] : 0;
      let a = l, b = r;          // metres from each formation edge to the carriageway
      if (g && g.apron > 0) {
        if (g.centre) {
          const side = nx[i] * (g.centre[0] - e.x[i]) + nz[i] * (g.centre[1] - e.z[i]) > 0 ? 1 : -1;
          if (side > 0) b += g.apron; else a += g.apron;
          stats.apron_sections++;
        } else stats.ring_no_centre_sections++;
      }
      // never past the other side: the carriageway keeps >= 0 width
      if (a + b > w) { const f = w / (a + b); a *= f; b *= f; }
      u[4 * k] = l / w; u[4 * k + 1] = a / w; u[4 * k + 2] = 1 - b / w; u[4 * k + 3] = 1 - r / w;
      if (l > 0 || r > 0) { sw = true; stats.sidewalk_sections++; if (k > 0) stats.sidewalk_m2 += (p.sec[k].s - p.sec[k - 1].s) * (l + r); }
      if (a > 0 || b > 0) plain = false;
    }
    if (sw) { stats.sidewalk_pieces++; swRuns.add(p.run); }
    if (g) { stats.ring_pieces++; stats.ring_sections += n; }
    const lanes = g && g.oneway ? g.lanes : 0;
    if (lanes) stats.oneway_ring_pieces++;
    return { u, lanes, plain };
  };
  const parts = [];
  for (let k = 0; k < drawn.pieces.length; k++) { parts.push(partOf(drawn.pieces[k])); if ((k & 255) === 255) yield; }
  stats.sidewalk_runs = swRuns.size;
  // the island kerb tops: the kerb face line (the island core, world/corridor
  // islandCore at the corridor's tap margin) to kerb_top_m inward, flat at
  // the face's top (islandEdgeY + kerb_m, emit.js kerbFaces)
  const kerbs = [];
  const tap = rings.some((g) => g.island) ? doc.corridor.rules.tap_margin_m : 0;
  rings.forEach((g, k) => {
    if (opts.kerbs === false || !g.island || !g.runs.length) return;
    const e = runs[g.runs[0]], RD = renderOf(e);
    const core = insetPoly(g.island, tap);
    const cpts = core.cx.map((x, i) => [x, core.cz[i]]);
    const inner = insetPoly(cpts, PAINT.kerb_top_m);
    const N = cpts.length, sec = [];
    let s = 0;
    for (let j = 0; j <= N; j++) {
      const i = j % N;
      if (j > 0) s += Math.hypot(core.cx[i] - core.cx[j - 1], core.cz[i] - core.cz[j - 1]);
      const y = islandEdgeY(g.island, core.cx[i], core.cz[i]) + g.kerb_m;
      sec.push({ i, s, lo: -PAINT.kerb_top_m / 2, hi: PAINT.kerb_top_m / 2, a: [core.cx[i], y, core.cz[i]], b: [inner.cx[i], y, inner.cz[i]], cap: 0 });
    }
    kerbs.push({ ring: k, run: g.runs[0], cls: e.cls, mat: RD.mat, view: RD.view, texLen: RD.texLen, rails: 0, w: PAINT.kerb_top_m, kerb: 1, sec });
    stats.kerbs++;
    stats.kerb_tris += 2 * N;
  });
  stats.sidewalk_m2 = Math.round(stats.sidewalk_m2);
  return { parts, kerbs, stats };
}

// ------------------------------------------------------------ the markings
// MARKS (owner: "a middle line between two opposite direction lanes, dashed
// lines between same direction lanes, something clever for forks", centre
// line dashed): the ribbon texture is plain asphalt; the client's paint
// shader draws the lane lines analytically, in metres (one width at every
// road width). LESS PAINT: no edge lines; a dashed centre line between the
// two directions, dashed dividers between lanes of one direction; no
// markings on a road whose class or carriageway is under the limits below.
// A LINE IS DRAWN ONLY WHERE ITS RIBBON IS THE ONLY SURFACE: wherever another
// road ribbon of the same or a higher rank covers a line position (a fork's
// stem, a crossing, a merge), the piece's lines stop, a setback either side
// (a fork's lines start where the ribbons part). A higher rank keeps its
// lines through a lower one's overlap.
const MARKS = {
  world_rev: 25,          // the first world rev whose car-road runs carry e.ln (world/profile.js lanesOf): from it on a garbled ln fails fast
  line_m: 0.15,           // line width (m)
  centre_dash_m: 3,       // the centre line (between the two directions): dash / gap (m)
  centre_gap_m: 6,
  lane_dash_m: 3,         // a divider between lanes of one direction: dash / gap (m)
  lane_gap_m: 6,
  min_cw_m: 5.5,          // no markings on a narrower carriageway
  min_lane_m: 2.5,        // nor where a lane would be narrower
  est_lane_m: 3.5,        // untagged: a one-way road's lanes = floor(cw / est_lane_m), a two-way road 1+1 (2+2 from 4 x this)
  max_lanes: 8,
  own_margin_m: 0.5,      // the ownership taps: each line position and +- this
  own_step_m: 1,          // ... every <= this along
  own_dy_m: 1.0,          // another ribbon counts as covering within this height (a crossing over or under it does not)
  setback_m: 1.0,         // lines stop this far before / after a covered tap
  grid_m: 16,
  // (D1-MARKS) GROUND MARKS AT A NODE: the look of ways.json marks[] (world/ways.js
  // roadMarks puts them; world/pathfield.mjs rasters them as P1 codes 5 zebra /
  // 6 xline / 7 grid; the painter and the ground shader draw them). Ground
  // paint only: no ribbon, no decal. Colours sRGB 0-255. The along extents
  // (zebra / grid length) are the world's (params ways.marks).
  ground: {
    xline_o_scale: 16,    // an xline's across distances (P1 G = o - o0, A = o1 - o) per byte step: 128 + 16 m holds 7.9 m from a bound (a one-way line up to 15.75 m; the d scale 32 held 3.94 m only)
    pad_m: 1.5,           // the field carries a mark's code this far past its rect: the four bilinear taps of every point inside it hold the code (1 m cells, any heading: <= sqrt 2)
    // zebra / grid: a run of n (odd) SLOTS of one pitch, a bar centred in each;
    // the phase (P1 G, A) counts slots modulo K = n + 2 margin_slots + 1 (P1.B
    // >> 3 = K / 2) with the run at slots margin .. margin + n - 1: the slot
    // index bounds the run across its pitch, P1.R (d scale) is the margin
    // along the bars only (no corner kink in a bilinear read). margin_slots
    // >= pad_m / pitch (the phase never wraps inside the pad); its size sets
    // the phase's metres per turn K pitch (8-bit cos / sin: quantisation ~ K
    // pitch / 800, the bilinear chord ~ the turn per 1 m cell cubed)
    zebra: { bar_m: 0.5, gap_m: 0.5, margin_slots: 2, colour: [226, 182, 34] },   // Swiss yellow bars parallel to the road axis, phased across it (slot = bar + gap), the middle one on the carriageway middle
    give_way: { line_m: 0.3, dash_m: 0.5, gap_m: 0.5, colour: [216, 216, 210] }, // white dashed across the approach lane(s); dashes from the nearer bound
    stop: { line_m: 0.5, colour: [216, 216, 210] },                            // white solid across the approach lane(s)
    // the give-way row of teeth (owner 2026-10-12, measured in Street View:
    // shark teeth): white teeth of base_m across x len_m along, gap_m apart
    // (period base + gap), n = floor((span + gap) / period) whole teeth
    // centred in the line's span, apexes upstream (toward the approaching
    // traffic), the base edge line_gap_m upstream of the line's upstream
    // edge. The line's own dashes are NOT drawn under a row (owner
    // 2026-10-12: "not dashes at the same time in the same section as where
    // the triangles are", supersedes "the line stays"): the decoders
    // (surfacegen _marks, groundmat gmMark) paint a line carrying teeth as
    // the teeth only; the field still carries it (its bounds place the row).
    // line_gap_m: the owner does not know it,
    // the smallest clean value. Main roads with a known direction only
    // (world/ways.js roadMarks). The field carries line + row as ONE mark
    // (world/pathfield.mjs markIndex): P1.B >> 3 = 2 + n, so max_n = 29.
    // max_span_m: the widest line that carries a row — the teeth decode the
    // across offset from BOTH bounds (P1 G and A), exact while every tap of
    // a tooth texel lies within (255 - d_off) / xline_o_scale = 7.94 m of
    // either bound: 7.94 - sqrt(2) (a tap's reach across) = 6.5 m; a wider
    // line keeps its dashes, its row is dropped (roadMarks tri_skipped.wide)
    give_way_tri: { base_m: 0.6, len_m: 0.5, gap_m: 0.3, line_gap_m: 0.3, max_n: 29, max_span_m: 6.5, colour: [216, 216, 210] },
    grid: { pitch_m: 0.15, bar_m: 0.07, margin_slots: 16, colour: [74, 77, 80], pit: [30, 30, 31] },  // cattle grid: steel bars across the way (slot = pitch) over a dark pit
  },
};
// the xline sub-kinds of a ways.json record; in the field (P1.B >> 3 of
// code 6) 1 give_way, 2 stop, TEETH_HB0 + n a give-way line carrying its
// row of n teeth (the give_way_tri record merged into its line)
const XLINE_SUB = { give_way: 1, stop: 2, give_way_tri: 3 };
const TEETH_HB0 = 2;
// the row of teeth: whole teeth in a span, the row's width, the row
// centre's distance upstream of its line's centre
function teethCount(span, T = MARKS.ground.give_way_tri) {
  return Math.min(T.max_n, Math.max(0, Math.floor((span + T.gap_m) / (T.base_m + T.gap_m) + 1e-9)));
}
function teethWidth(n, T = MARKS.ground.give_way_tri) { return n * T.base_m + (n - 1) * T.gap_m; }
function teethBack(G = MARKS.ground) { return G.give_way.line_m / 2 + G.give_way_tri.line_gap_m + G.give_way_tri.len_m / 2; }
// the teeth at a decoded field point: n teeth, u the along offset upstream of
// the line centre, x the across offset from the row's left edge -> {k (the
// nearest tooth), cx (|x - its axis|), hw (the half base at u: base / 2 x the
// fraction from the apex), u0 / u1 (the row's along band)}. ONE model: the
// painter (surfacegen _marks), the shader (gmMark) and shaderprobe mirror it
function teethAt(n, u, x, G = MARKS.ground) {
  const T = G.give_way_tri, P = T.base_m + T.gap_m;
  const u0 = G.give_way.line_m / 2 + T.line_gap_m, u1 = u0 + T.len_m;
  const k = Math.min(n - 1, Math.max(0, Math.floor((x + T.gap_m / 2) / P)));
  const cx = Math.abs(x - k * P - T.base_m / 2);
  const hw = (T.base_m / 2) * Math.min(1, Math.max(0, (u1 - u) / T.len_m));
  return { k, cx, hw, u0, u1, inside: u >= u0 && u <= u1 && cx <= hw };
}

// (D1-MARKS) a ways.json mark's local frame: centre c (on the run's
// centreline at the node), unit tangent t (+s), normal n (+o: (-tz, tx), the
// run frame's), the across bounds [o0, o1] (run offsets) and the along half
// length h. ONE source: the field (world/pathfield.mjs), the gates and
// check-draw read it. -> {kind, sub, c, t, n, o0, o1, h} or throws on a
// garbled record.
function markGeom(m) {
  const num = (v) => typeof v === 'number' && Number.isFinite(v);
  const bad = (f) => { throw new Error(`ways.json mark (node ${m && m.node}, run ${m && m.run}): ${f}`); };
  if (!m || !['zebra', 'xline', 'grid'].includes(m.kind)) bad(`kind ${m && m.kind}`);
  if (!Array.isArray(m.at) || m.at.length !== 2 || !m.at.every(num)) bad('at');
  if (!Array.isArray(m.dir) || m.dir.length !== 2 || !m.dir.every(num)) bad('dir');
  if (!Array.isArray(m.o) || m.o.length !== 2 || !m.o.every(num) || !(m.o[1] > m.o[0])) bad('o');
  if (!(num(m.len) && m.len > 0)) bad('len');
  if (m.kind === 'xline' && !(m.sub in XLINE_SUB)) bad(`sub ${m.sub}`);
  if (m.sub === 'give_way_tri') {
    const T = MARKS.ground.give_way_tri;
    if (!(m.side === 1 || m.side === -1)) bad('a row of teeth without its side (+-1)');
    if (!(Number.isInteger(m.n) && m.n >= 1 && m.n <= T.max_n)) bad(`a row of ${m.n} teeth (1 .. ${T.max_n})`);
    if (!(Math.abs(m.o[1] - m.o[0] - teethWidth(m.n)) <= 5e-3)) bad(`a row of ${m.n} teeth ${m.o[1] - m.o[0]} m wide, not ${teethWidth(m.n)}`);
    if (!(Math.abs(m.len - T.len_m) <= 1e-6 && num(m.back) && Math.abs(m.back - teethBack()) <= 5e-3)) bad(`a row of teeth len ${m.len} back ${m.back}: not len_m ${T.len_m}, back ${teethBack()}`);
  }
  const l = Math.hypot(m.dir[0], m.dir[1]);
  if (!(l > 0.5)) bad('dir not a unit vector');
  const tx = m.dir[0] / l, tz = m.dir[1] / l;
  const g = { kind: m.kind, sub: m.kind === 'xline' ? m.sub : null, c: [m.at[0], m.at[1]], t: [tx, tz], n: [-tz, tx], o0: m.o[0], o1: m.o[1], h: m.len / 2 };
  if (m.sub === 'give_way_tri') { g.side = m.side; g.n_teeth = m.n; }
  if (m.kind !== 'xline') {
    // the slot run: zebra across (o), grid along (len); n odd, the phase
    // inside [0, slots_k) over the whole pad
    const G = MARKS.ground, Q = G[m.kind], p = m.kind === 'zebra' ? Q.bar_m + Q.gap_m : Q.pitch_m;
    const ext = m.kind === 'zebra' ? m.o[1] - m.o[0] : m.len, n = Math.round(ext / p), K = n + 2 * Q.margin_slots + 1;
    if (!(Math.abs(n * p - ext) <= 5e-3 && n % 2 === 1)) bad(`${m.kind === 'zebra' ? 'o' : 'len'} ${ext} is no odd count of ${p} m slots`);
    if (!(K <= 62)) bad(`${n} slots: the phase turn K ${K} > 62`);
    g.slot = { p, n, m: Q.margin_slots, K, u0: m.kind === 'zebra' ? m.o[0] : -m.len / 2 };
  }
  return g;
}
// a point's local coordinates in a markGeom frame: a along (+t), o across
// (+n, the run offset) and the signed rect distance d (+ inside: the smaller
// of the along and across margins)
function markLocal(g, x, z) {
  const dx = x - g.c[0], dz = z - g.c[1];
  const a = dx * g.t[0] + dz * g.t[1], o = dx * g.n[0] + dz * g.n[1];
  return { a, o, d: Math.min(g.h - Math.abs(a), o - g.o0, g.o1 - o) };
}
// the zebra's bar centres (run offsets) inside [o0, o1]: bars of bar_m every
// bar_m + gap_m, one centred on the middle of the carriageway
function zebraBars(g, Z = MARKS.ground.zebra) {
  const P = Z.bar_m + Z.gap_m, om = (g.o0 + g.o1) / 2, out = [];
  for (let k = -Math.ceil((om - g.o0) / P); om + k * P <= g.o1; k++) {
    const c = om + k * P;
    if (c - Z.bar_m / 2 >= g.o0 - 1e-9 && c + Z.bar_m / 2 <= g.o1 + 1e-9) out.push(c);
  }
  return out;
}
// the highways that carry markings (OSM highway, world e.ln.hw) and the rank
// of every car road (a higher rank keeps its lines through a lower one's
// overlap). A world before MARKS.world_rev (no e.ln): render class major
// carries markings at rank 4, minor ranks 2.
const MARK_HW = new Set(['motorway', 'trunk', 'primary', 'secondary', 'tertiary', 'unclassified',
  'motorway_link', 'trunk_link', 'primary_link', 'secondary_link', 'tertiary_link']);
const RANK = {
  motorway: 7, trunk: 6, primary: 5, secondary: 4, tertiary: 3, unclassified: 2, residential: 2, road: 2, busway: 2, bus_guideway: 2,
  motorway_link: 5, trunk_link: 4, primary_link: 3, secondary_link: 2.5, tertiary_link: 2, service: 1, living_street: 1,
};

// run e's lane layout {b, f, rank} (b lanes against the run on its left, -o;
// f with it on its right, +o: right-hand traffic) or {b: 0, f: 0, rank}: no
// lines. Fails fast on a garbled e.ln. (SAN-GIAN) solid: true when the
// centre line (between the two directions) is SOLID — e.ln.cl 'solid'
// (world/profile.js lanesOf: divider=line, a sibling plate's merged way);
// absent otherwise (the dashed centre line).
function laneLayout(e, rings, worldRev) {
  const ln = e.ln;
  const has = Number.isFinite(worldRev) && worldRev >= MARKS.world_rev;
  if (ln !== undefined) {
    const int = (v, lo) => v === null || (Number.isInteger(v) && v >= lo && v <= MARKS.max_lanes);
    if (!ln || typeof ln.hw !== 'string' || !int(ln.n, 1) || !int(ln.f, 0) || !int(ln.b, 0) || ![-1, 0, 1].includes(ln.ow) || (ln.cl !== undefined && ln.cl !== 'solid')) {
      throw new Error(`ways.json run (chain ${e.chain}, way ${e.way}): ln ${JSON.stringify(ln)} is not {hw, n, f, b, ow}`);
    }
  }
  const legacy = !has && ln === undefined;
  const hw = ln ? ln.hw : null;
  const rank = ln ? (RANK[hw] || 0) : legacy ? (e.cls === 'major' ? 4 : e.cls === 'minor' ? 2 : 0) : 0;
  const none = { b: 0, f: 0, rank };
  const g = e.ring != null && rings ? rings[e.ring] : null;
  if (!(ln ? MARK_HW.has(hw) || g : legacy && e.cls === 'major')) return none;
  const cw = g ? g.cw : (e.cw || e.w);
  if (!(cw >= MARKS.min_cw_m)) return none;
  let ow = ln ? ln.ow : 0;
  if (g && g.oneway) ow = e.oneway === -1 ? -1 : 1;
  let n = g && g.oneway ? g.lanes : ln && ln.n;
  let f = null, b = null;
  if (ow) {
    if (!n) n = ln && ln.f !== null && ln.b !== null ? ln.f + ln.b : Math.max(1, Math.floor(cw / MARKS.est_lane_m));
    if (ow > 0) { f = n; b = 0; } else { f = 0; b = n; }
  } else if (ln && (ln.f !== null || ln.b !== null)) {
    f = ln.f; b = ln.b;
    if (f === null) f = n ? Math.max(0, n - b) : b;
    if (b === null) b = n ? Math.max(0, n - f) : f;
  } else if (n) {
    if (n < 2) return none;
    b = Math.floor(n / 2); f = n - b;
  } else {
    b = f = cw >= 4 * MARKS.est_lane_m ? 2 : 1;
  }
  if (f + b < 2 || cw / (f + b) < MARKS.min_lane_m) return none;
  return ln && ln.cl === 'solid' && b > 0 && f > 0 ? { b, f, rank, solid: true } : { b, f, rank };
}

// the lane-line offsets (run-relative o, ascending) of run e at station i
// with n = b + f lanes: the carriageway [-h + left strip (+ ring apron),
// h - right strip (- apron)] split into n lanes. ONE source: laneMarks' taps
// and the WAYS-G field (world/pathfield.mjs) both read it.
function laneLines(e, i, n, rings, F) {
  const h = e.w / 2;
  const l = e.sw ? e.sw[i][0] : 0, rr = e.sw ? e.sw[i][1] : 0;
  let c0 = -h + l, c1 = h - rr;
  const g = e.ring != null && rings ? rings[e.ring] : null;
  if (g && g.apron > 0 && g.centre) {
    const side = F.nx[i] * (g.centre[0] - e.x[i]) + F.nz[i] * (g.centre[1] - e.z[i]) > 0 ? 1 : -1;
    if (side > 0) c1 -= g.apron; else c0 += g.apron;
  }
  const out = [];
  for (let k = 1; k < n; k++) out.push(c0 + ((c1 - c0) * k) / n);
  return out;
}

// laneMarks(doc, pieces, frames, {worldRev}) -> {parts: per piece {b, f,
//   cut: Float64Array(2 per section) — section q's [h0, h1]: the hidden
//   arc interval (run metres, the section's s) of the quad (q-1, q), h1 < h0
//   none; solid: true when its centre line is solid (laneLayout); null for
//   a piece without lines}, stats}
// pieces: drawnRibbons' + deckRibbons' (all of them: the ownership taps
// test every road ribbon); a line position is covered where another run's
// piece (not the run itself nor its chain neighbour) of rank >= the run's
// holds the tap within own_dy_m of its height, on the triangles the GPU
// draws (tri's diagonal).
function laneMarks(doc, pieces, frames, opts = {}) { return drain(laneSteps(doc, pieces, frames, opts)); }
function* laneSteps(doc, pieces, frames, opts = {}) {
  const runs = doc.ways, rings = Array.isArray(doc.rings) ? doc.rings : null;
  const joins = chainJoins(doc);
  const stats = { pieces: 0, marked_pieces: 0, centre_pieces: 0, divider_pieces: 0, tagged_runs: 0, est_runs: 0, marked_m: 0, hidden_m: 0, cut_quads: 0, tapped_quads: 0 };
  if (Number.isFinite(opts.worldRev) && opts.worldRev >= MARKS.world_rev && runs.some((e) => e.cls === 'major') && !runs.some((e) => e.ln)) {
    throw new Error(`ways.json of world rev ${opts.worldRev} carries no run ln (>= ${MARKS.world_rev}: a car road's lane tags expected, world/profile.js lanesOf)`);
  }
  yield;
  const L = runs.map((e) => laneLayout(e, rings, opts.worldRev));
  yield;
  runs.forEach((e, r) => { if (L[r].f + L[r].b >= 2) { if (e.ln && (e.ln.n || e.ln.f !== null || e.ln.b !== null || e.ln.ow)) stats.tagged_runs++; else stats.est_runs++; } });
  // the quad index of every road piece of rank >= 1
  const B = MARKS.grid_m, grid = new Map();
  const key = (bx, bz) => (bx + 4096) * 16384 + (bz + 4096);
  const quads = [];   // [piece, q, x0, z0, x1, z1]
  const indexPiece = (p, k) => {
    if (p.kerb || p.rails || !(L[p.run].rank >= 1)) return;
    for (let q = 0; q + 1 < p.sec.length; q++) {
      const A = p.sec[q], C = p.sec[q + 1];
      const x0 = Math.min(A.a[0], A.b[0], C.a[0], C.b[0]), x1 = Math.max(A.a[0], A.b[0], C.a[0], C.b[0]);
      const z0 = Math.min(A.a[2], A.b[2], C.a[2], C.b[2]), z1 = Math.max(A.a[2], A.b[2], C.a[2], C.b[2]);
      const id = quads.length;
      quads.push([k, q, x0, z0, x1, z1]);
      for (let bx = Math.floor(x0 / B); bx <= Math.floor(x1 / B); bx++) {
        for (let bz = Math.floor(z0 / B); bz <= Math.floor(z1 / B); bz++) {
          const kk = key(bx, bz);
          let a = grid.get(kk);
          if (!a) grid.set(kk, a = []);
          a.push(id);
        }
      }
    }
  };
  for (let k = 0; k < pieces.length; k++) { indexPiece(pieces[k], k); if ((k & 63) === 63) yield; }
  yield;
  const stamp = new Uint32Array(quads.length);
  let tick = 0;
  // (x, z) on quad Q's drawn triangles -> its height there, else NaN
  const onQuad = (Q, x, z) => {
    const p = pieces[Q[0]], A = p.sec[Q[1]], C = p.sec[Q[1] + 1];
    const tr = (P0, P1, P2) => {
      const d = (P1[2] - P2[2]) * (P0[0] - P2[0]) + (P2[0] - P1[0]) * (P0[2] - P2[2]);
      if (Math.abs(d) < 1e-12) return NaN;
      const l1 = ((P1[2] - P2[2]) * (x - P2[0]) + (P2[0] - P1[0]) * (z - P2[2])) / d;
      const l2 = ((P2[2] - P0[2]) * (x - P2[0]) + (P0[0] - P2[0]) * (z - P2[2])) / d, l3 = 1 - l1 - l2;
      return l1 < 0 || l2 < 0 || l3 < 0 ? NaN : l1 * P0[1] + l2 * P1[1] + l3 * P2[1];
    };
    const y = tr(A.a, A.b, C.a);
    return Number.isFinite(y) ? y : tr(A.b, C.b, C.a);
  };
  const partOf = (p) => {
    stats.pieces++;
    const e = runs[p.run], Ly = L[p.run], n = Ly.b + Ly.f;
    if (p.kerb || p.rails || n < 2) return null;
    stats.marked_pieces++;
    if (Ly.b && Ly.f) stats.centre_pieces++;
    if (Ly.b > 1 || Ly.f > 1) stats.divider_pieces++;
    const S = p.sec, h = e.w / 2;
    // the line positions (o, run-relative) at station i: the carriageway
    // [-h + left strip, h - right strip] split into n lanes
    const offs = (i) => {
      const out = [];
      for (const o of laneLines(e, i, n, rings, frames[p.run])) out.push(o - MARKS.own_margin_m, o, o + MARKS.own_margin_m);
      return out;
    };
    const hits = [];
    for (let q = 0; q + 1 < S.length; q++) {
      const A = S[q], C = S[q + 1];
      stats.marked_m += C.s - A.s;
      const x0 = Math.min(A.a[0], A.b[0], C.a[0], C.b[0]), x1 = Math.max(A.a[0], A.b[0], C.a[0], C.b[0]);
      const z0 = Math.min(A.a[2], A.b[2], C.a[2], C.b[2]), z1 = Math.max(A.a[2], A.b[2], C.a[2], C.b[2]);
      if (++tick === 0xffffffff) { stamp.fill(0); tick = 1; }
      const cand = [];
      for (let bx = Math.floor(x0 / B); bx <= Math.floor(x1 / B); bx++) {
        for (let bz = Math.floor(z0 / B); bz <= Math.floor(z1 / B); bz++) {
          const a = grid.get(key(bx, bz));
          if (!a) continue;
          for (const id of a) {
            if (stamp[id] === tick) continue;
            stamp[id] = tick;
            const Q = quads[id], o = pieces[Q[0]].run;
            if (o === p.run || o === joins.pred[p.run] || o === joins.succ[p.run] || L[o].rank < Ly.rank) continue;
            if (Q[2] > x1 || Q[4] < x0 || Q[3] > z1 || Q[5] < z0) continue;
            cand.push(Q);
          }
        }
      }
      if (!cand.length) continue;
      stats.tapped_quads++;
      const len = Math.max(Math.hypot(C.a[0] - A.a[0], C.a[2] - A.a[2]), Math.hypot(C.b[0] - A.b[0], C.b[2] - A.b[2]), C.s - A.s);
      const nf = Math.max(1, Math.ceil(len / MARKS.own_step_m));
      const oA = offs(A.i), oC = offs(C.i);
      for (let t = 0; t <= nf; t++) {
        const f = t / nf;
        let hit = false;
        for (let m = 0; m < oA.length && !hit; m++) {
          // the tap: offset o at both sections, interpolated along
          const at = (Sx, o) => { const g = (o - Sx.lo) / Math.max(1e-6, Sx.hi - Sx.lo); return [Sx.a[0] + g * (Sx.b[0] - Sx.a[0]), Sx.a[1] + g * (Sx.b[1] - Sx.a[1]), Sx.a[2] + g * (Sx.b[2] - Sx.a[2]), g]; };
          const PA = at(A, oA[m]), PC = at(C, oC[m]);
          if ((PA[3] < 0 || PA[3] > 1) && (PC[3] < 0 || PC[3] > 1)) continue;   // off this ribbon (a clipped section)
          const x = PA[0] + f * (PC[0] - PA[0]), y = PA[1] + f * (PC[1] - PA[1]), z = PA[2] + f * (PC[2] - PA[2]);
          for (const Q of cand) {
            const yq = onQuad(Q, x, z);
            if (Number.isFinite(yq) && Math.abs(yq - y) <= MARKS.own_dy_m) { hit = true; break; }
          }
        }
        if (hit) hits.push(A.s + f * (C.s - A.s));
      }
    }
    // the hidden intervals: each covered tap +- half a step + the setback, merged
    const iv = [];
    const pad = MARKS.setback_m;
    for (const sh of hits) {
      const a = sh - pad - MARKS.own_step_m / 2, b = sh + pad + MARKS.own_step_m / 2;
      if (iv.length && a <= iv[iv.length - 1][1]) iv[iv.length - 1][1] = Math.max(iv[iv.length - 1][1], b);
      else iv.push([a, b]);
    }
    const cut = new Float64Array(2 * S.length);
    let j = 0;
    for (let q = 0; q < S.length; q++) {
      cut[2 * q] = 1; cut[2 * q + 1] = -1;
      if (q === 0) continue;
      const s0 = Math.min(S[q - 1].s, S[q].s), s1 = Math.max(S[q - 1].s, S[q].s);
      while (j < iv.length && iv[j][1] < s0) j++;
      let h0 = Infinity, h1 = -Infinity;
      for (let k = j; k < iv.length && iv[k][0] <= s1; k++) { h0 = Math.min(h0, iv[k][0]); h1 = Math.max(h1, iv[k][1]); }
      if (h0 <= h1) {
        cut[2 * q] = h0; cut[2 * q + 1] = h1;
        stats.cut_quads++;
        stats.hidden_m += Math.max(0, Math.min(s1, h1) - Math.max(s0, h0));
      }
    }
    return Ly.solid ? { b: Ly.b, f: Ly.f, cut, solid: true } : { b: Ly.b, f: Ly.f, cut };
  };
  const parts = [];
  for (let k = 0; k < pieces.length; k++) { parts.push(partOf(pieces[k])); if ((k & 15) === 15) yield; }
  stats.marked_m = Math.round(stats.marked_m);
  stats.hidden_m = Math.round(stats.hidden_m);
  return { parts, layout: L, stats };
}

// ------------------------------------------------------------ the decks
// deckRibbons(doc, drawn) -> {pieces (piece-shaped, deck: 1), stats}
// (MARKS, owner: "the surface doesn't look right over bridges") a road run's
// DECK stations (kind 1) are drawn as its own ribbon too, on the deck top
// (ways.json decks[]: top + cf o, clipped to the top's edges eL / eR), joined
// to the drawn approach sections either side — the same asphalt and lines
// as the road on the ground. Roads only (render major / minor): a path's
// bridge keeps its structure top, a rail's deck its structplan rails. Bores
// are not drawn (the ribbon material is unlit: it would glow in the bore).
// (deck-top-is-its-way) only on a deck whose top_mat is DECK_RIBBON.top (the
// carried way's look is asphalt): a sett / gravel road's deck shows its own
// top (world/emit.js deckTop). A deck without top_mat (a world baked before
// the row) draws as it did, every road deck ribboned (as PAINT.world_rev: the
// rebuilt client keeps the live bake drawing until its Build is made live).
const DECK_RIBBON = { lift_m: 0.01, match_m: 0.05, cls: new Set(['major', 'minor']), top: 'tarmac' };
function deckRibbons(doc, drawn) {
  const runs = doc.ways, F = drawn.frames;
  const stats = { pieces: 0, sections: 0, joined: 0, unmatched: 0, lone: 0, deck_m: 0 };
  const pieces = [];
  if (!Array.isArray(doc.decks) || !doc.decks.length) return { pieces, stats };
  // every deck station by position (1 m cells)
  const cell = new Map();
  doc.decks.forEach((d, k) => {
    if (!Array.isArray(d.x) || (d.top_mat !== undefined && d.top_mat !== DECK_RIBBON.top)) return;
    for (let j = 0; j < d.x.length; j++) {
      const kk = Math.floor(d.x[j]) + ':' + Math.floor(d.z[j]);
      let a = cell.get(kk);
      if (!a) cell.set(kk, a = []);
      a.push(k, j);
    }
  });
  const find = (x, z) => {
    let best = null, bd = DECK_RIBBON.match_m;
    for (let dx = -1; dx <= 1; dx++) {
      for (let dz = -1; dz <= 1; dz++) {
        const a = cell.get((Math.floor(x) + dx) + ':' + (Math.floor(z) + dz));
        if (!a) continue;
        for (let t = 0; t < a.length; t += 2) {
          const d = doc.decks[a[t]], j = a[t + 1], dd = Math.hypot(d.x[j] - x, d.z[j] - z);
          if (dd <= bd) { bd = dd; best = [d, j]; }
        }
      }
    }
    return best;
  };
  // the drawn sections at a piece end, by run and station
  const ends = new Map();
  for (const p of drawn.pieces) {
    for (const c of [p.sec[0], p.sec[p.sec.length - 1]]) if (!c.cap) ends.set(p.run + ':' + c.i, c);
  }
  runs.forEach((e, r) => {
    if (!DECK_RIBBON.cls.has(e.cls) || !e.kind || e.x.length < 2) return;
    const n = e.x.length, { nx, nz } = F[r], h = e.w / 2, RD = renderOf(e);
    const s = new Float64Array(n);
    for (let i = 1; i < n; i++) s[i] = s[i - 1] + Math.hypot(e.x[i] - e.x[i - 1], e.z[i] - e.z[i - 1]);
    const deckSec = (i) => {
      const m = find(e.x[i], e.z[i]);
      if (!m) return null;
      const [d, j] = m, dot = nx[i] * d.nx[j] + nz[i] * d.nz[j];
      const sg = dot < 0 ? -1 : 1;
      // the run's offsets o on the deck's own axis: o_deck = sg o
      const lo = Math.max(-h, sg > 0 ? -d.eL[j] : -d.eR[j]), hi = Math.min(h, sg > 0 ? d.eR[j] : d.eL[j]);
      if (!(hi - lo >= DRAW.min_lane_m)) return null;
      const v = (o) => [e.x[i] + nx[i] * o, d.top[j] + d.cf[j] * sg * o + DECK_RIBBON.lift_m, e.z[i] + nz[i] * o];
      return { i, s: s[i], lo, hi, a: v(lo), b: v(hi), cap: 0 };
    };
    let cur = null;
    const flush = (endI) => {
      if (cur && cur.length) {
        const c = endI !== undefined ? ends.get(r + ':' + endI) : null;
        if (c) { cur.push(c); stats.joined++; }
        if (cur.length >= 2) {
          pieces.push({ run: r, cls: e.cls, mat: RD.mat, view: RD.view, texLen: RD.texLen, rails: 0, w: e.w, deck: 1, sec: cur });
          stats.pieces++;
          stats.sections += cur.length;
          stats.deck_m += cur[cur.length - 1].s - cur[0].s;
        } else stats.lone++;    // one deck station, no approach drawn either side
      }
      cur = null;
    };
    for (let i = 0; i < n; i++) {
      const isDeck = e.flag[i] !== 0 && e.kind[i] === 1;
      if (!isDeck) { flush(i); continue; }
      const c = deckSec(i);
      if (!c) { stats.unmatched++; flush(); continue; }
      if (!cur) {
        cur = [];
        const a = i > 0 ? ends.get(r + ':' + (i - 1)) : null;
        if (a) { cur.push(a); stats.joined++; }
      }
      cur.push(c);
    }
    flush();
  });
  stats.deck_m = Math.round(stats.deck_m);
  return { pieces, stats };
}

// ------------------------------------------------------------ the triangles
// the point of quad (sections S, T) at (f along, g across) ON the triangle the
// GPU draws: vertices A0 = S.a, A1 = S.b, B0 = T.a, B1 = T.b; triangles
// (A0, A1, B0) and (A1, B1, B0) — diagonal A1 -> B0
function tri(S, T, f, g, out) {
  const A0 = S.a, A1 = S.b, B0 = T.a, B1 = T.b;
  if (f + g <= 1) {
    for (let c = 0; c < 3; c++) out[c] = A0[c] + f * (B0[c] - A0[c]) + g * (A1[c] - A0[c]);
  } else {
    for (let c = 0; c < 3; c++) out[c] = B1[c] + (1 - f) * (A1[c] - B1[c]) + (1 - g) * (B0[c] - B1[c]);
  }
  return out;
}

// every tap of a piece: per quad <= along_m along and <= across_m across,
// on the drawn triangles -> fn(x, y, z, station i, lateral offset o)
function pieceTaps(piece, fn, along_m = 1, across_m = 0.5) {
  const S = piece.sec, out = [0, 0, 0];
  const d2 = (p, q) => Math.hypot(q[0] - p[0], q[2] - p[2]);
  for (let k = 0; k + 1 < S.length; k++) {
    const A = S[k], B = S[k + 1];
    const nf = Math.max(1, Math.ceil(Math.max(d2(A.a, B.a), d2(A.b, B.b)) / along_m));
    const ng = Math.max(1, Math.ceil(Math.max(d2(A.a, A.b), d2(B.a, B.b)) / across_m));
    const last = k + 2 === S.length;
    for (let a = 0; a <= (last ? nf : nf - 1); a++) {
      const f = a / nf;
      const st = f < 0.5 ? A.i : B.i;
      for (let b = 0; b <= ng; b++) {
        const g = b / ng;
        tri(A, B, f, g, out);
        const o = (A.lo + g * (A.hi - A.lo)) * (1 - f) + (B.lo + g * (B.hi - B.lo)) * f;
        fn(out[0], out[1], out[2], st, o);
      }
    }
  }
}

// the rails of a rail / funicular piece: the centreline polyline [x, y, z]
// (o = 0 when the section holds it, else the section's middle)
function pieceCentre(piece) {
  return piece.sec.map((c) => {
    if (c.lo <= 0 && c.hi >= 0 && c.hi > c.lo) {
      const g = -c.lo / (c.hi - c.lo);
      return [c.a[0] + g * (c.b[0] - c.a[0]), c.a[1] + g * (c.b[1] - c.a[1]), c.a[2] + g * (c.b[2] - c.a[2])];
    }
    return [(c.a[0] + c.b[0]) / 2, (c.a[1] + c.b[1]) / 2, (c.a[2] + c.b[2]) / 2];
  });
}

// the bike graph / HUD names: every surface run as a drawn polyline
// {roads: [{pts, cls, name, run}], rails: [{pts, run}]} (client/src/roadgraph.js);
// `run` = the run's index in doc.ways (TRAILS: RoadGraph.nearest -> run ->
// the client's trail index, client/src/trailmarks.js)
function graphInput(doc) { return drain(graphSteps(doc)); }
function* graphSteps(doc) {
  const roads = [], rails = [];
  for (let run = 0; run < doc.ways.length; run++) {
    if ((run & 255) === 255) yield;
    const e = doc.ways[run];
    let cur = null;
    const flush = () => { if (cur && cur.length >= 2) (e.cls === 'rail' || e.cls === 'funicular' ? rails.push({ pts: cur, run }) : roads.push({ pts: cur, cls: e.cls === 'stairs' ? 'path' : e.cls, name: e.name || '', run })); cur = null; };
    for (let i = 0; i < e.x.length; i++) {
      if (e.kind[i] !== 0) { flush(); continue; }
      (cur || (cur = [])).push([e.x[i], e.y[i], e.z[i]]);
    }
    flush();
  }
  return { roads, rails };
}

// ------------------------------------------------------------ the forks
// S7 D-2 (owner O9 "find a way to understand where train tracks join and
// model proper forks there"): the DRAW CONTRACT of ways.json `forks[]`
// (world/ways.js, the comment above railGraph). Rules:
//   A RAIL JUNCTION IS A TURNOUT — the through rails run on, bit-exact, over
//     the node; the diverging leg's inner rail starts at the node as a BLADE
//     lying against the stock rail, its outer rail parts from the through
//     rail where the two stop overlapping; the inner rail crosses the through
//     rail at the FROG (a casting over the crossing, its top over BOTH
//     heads there), which is frog_s; one
//     ballast bed: each diverging leg's ribbon is TRIMMED over [0, clear_s]
//     to what no other rail ribbon already covers (the shared bed drawn
//     once). ONE BALLAST BED PER GROUP: past the windows, a rail quad whose
//     bed still overlaps another run's (a ladder's neighbouring tracks
//     closer than a bed, a leg past its clear_s) is trimmed the same way, in
//     (run, station) order, so no two rail ribbons overlap anywhere.
//   A GAP IS ONE BED — a 'gap' record's two dead ends are joined by one
//     ribbon quad (trimmed against every bed, its own runs' included) and
//     their rails by one segment each, only where the gap FACES along both
//     tracks (each end's outward tangent within FORK.gap_face_deg of the
//     line to the other end). Two dead ends side by side (a lateral gap)
//     are not one track: they stay unjoined, listed (forks[].why).
//   A RAIL PIECE END MEETING ANOTHER SHARES ONE SECTION — two rail piece
//     ends at one node (FORK.node_m) take one cross-section (vertices, rail
//     points and rail normals bit-identical), so no wedge overlaps and no
//     rail kink at a track's run joins; at a fork node the through pair
//     shares it and the diverging legs start on it.
//   A RAIL IS NEVER A SIDE WAY OF A RAIL — a rail station drawnRibbons cut
//     against a box a RAIL dominates (the mouthAnchors box of a fork; a
//     level crossing's road may share the box) is drawn on its own lane
//     again: the trim, not the junction cap, makes one bed.
//   A TRACK DRAWS AT ITS RECORDED GAUGE — run.gauge_m (S6.d), else the
//     gauge its track's fork group records (forks[].legs[].gauge_m), else
//     RENDER's class default. ONE resolution, `railGauges(doc)`: the surface
//     (forkDraw -> ways2) and the decks / bores (structplan railRuns) both
//     read it, so every ground <-> structure rail join is at one gauge.
//   A TURNOUT ON A DECK IS DRAWN BY THE DECK'S RAIL RUNS — a fork with a
//     through / leg node station on a structure (flag != 0: deck / bore) is
//     not drawn here (forks[].on_structure): structplan railRuns draws it
//     over the deck stations with these same kernels (forkRails, railPoint).
// drawnRibbons is NOT changed (every bake-side tap reads the same triangles,
// DRAW_REV stays 1): `forkDraw` is a pass over its output. The geometry
// kernels are pure and frame-based (`forkRails`, `trimBed`, `sharedSection`)
// so the deck draw (client/src/structplan.js) can call them on deck stations.
const FORK = {
  world_rev: 20,          // the first world rev whose ways.json carries forks[] (S6.d B4c-4): from it a missing / garbled forks[] fails fast
  node_m: 0.01,           // A RAIL PIECE END MEETING ANOTHER SHARES ONE SECTION: piece ends on one node key of this size (world/ways.js railGraph's 1 cm key) are one node
  rail_w: 0.14,           // rail head width (client rail extrusion 2 x 0.07, roads3d buildRails RW): rails closer than this centre to centre overlap
  rail_h: 0.14,           // rail height (roads3d buildRails RH)
  frog_w: 0.3,            // the frog casting's width (m), centred on the crossing
  frog_pad_m: 0.3,        // the casting runs on this far past the rails' coplanar overlap at each end (m)
  frog_max_m: 8,          // the longest frog casting (a 1 deg crossing's overlap is 8 m)
  frog_lift_m: 0.004,     // the casting's top over the rail head (covers the two coplanar heads)
  area_eps_m2: 1e-8,      // a trimmed bed piece smaller than this is dropped (float dust of the cut lines)
  overlap_eps_m2: 1e-6,   // check-draw: two rail ribbon triangles sharing less than this do not overlap (the trim's cut lines are float-exact to ~1e-12)
  index_m: 8,             // the trim's triangle index cell (m)
  gap_face_deg: 30,       // A GAP IS ONE BED only between FACING dead ends: each end's outward tangent within this of the line to the other end. A lateral pair (two tracks side by side, the line between the ends ~80-100 deg off their tangents) joined end to end draws its rails crossing as an X over the bed. DEFERRED (D-2c review B'1): an angle calls a short gap with a small sideways offset lateral (0.29 m on a 0.5 m gap); a perpendicular-offset rule (lateral if the offset across A's tangent > ~0.75 m, half a track spacing, or angle > 30 deg AND offset > 0.3 m) would keep those joined — no such gap on the current worlds
};

// the fork records of a doc, or null (a world older than FORK.world_rev with
// no forks[]: legacy rails). Fails fast where a world claims them and garbles
// one.
function forkDoc(doc, worldRev) {
  if (!Array.isArray(doc.forks)) {
    if (doc.forks !== undefined && doc.forks !== null) throw new Error('ways.json: forks is not an array');
    if (Number.isFinite(worldRev) && worldRev >= FORK.world_rev) {
      throw new Error(`ways.json of world rev ${worldRev} carries no forks (>= ${FORK.world_rev}: forks[] expected, S6.d B4c-4)`);
    }
    return null;
  }
  const num = (v) => typeof v === 'number' && Number.isFinite(v);
  const N = doc.forks.length;
  doc.forks.forEach((f, k) => {
    const bad = (w) => { throw new Error(`ways.json forks[${k}]: ${w}`); };
    if (!f || !['turnout', 'double', 'gap'].includes(f.kind)) bad(`kind ${JSON.stringify(f && f.kind)}`);
    if (!Array.isArray(f.node) || f.node.length !== 2 || !num(f.node[0]) || !num(f.node[1])) bad('node');
    if (!Array.isArray(f.through) || f.through.length !== 2) bad('through (two legs)');
    if (!Array.isArray(f.legs)) bad('legs');
    if (!Number.isInteger(f.group) || f.group < 0 || f.group >= N || doc.forks[f.group].group !== f.group) bad(`group ${f.group}`);
    if (!(num(f.clear_m) && f.clear_m >= 0)) bad('clear_m');
    const leg = (l, what) => {
      if (!l || !Number.isInteger(l.run) || l.run < 0 || l.run >= doc.ways.length) bad(`${what}.run`);
      const e = doc.ways[l.run];
      if (!(renderOf(e).rails > 0)) bad(`${what}.run ${l.run} is not a rail run (${e.cls})`);
      if (!Number.isInteger(l.i) || l.i < 0 || l.i >= e.x.length) bad(`${what}.i`);
      if (l.dir !== 1 && l.dir !== -1) bad(`${what}.dir`);
    };
    f.through.forEach((l, j) => leg(l, `through[${j}]`));
    if (f.kind === 'gap') {
      if (f.legs.length) bad('a gap carries no diverging legs');
      if (!(num(f.gap_m) && f.gap_m > 0)) bad('gap_m');
    } else {
      if (!f.legs.length) bad(`a ${f.kind} without a diverging leg`);
      f.legs.forEach((l, j) => {
        leg(l, `legs[${j}]`);
        if (l.of !== 0 && l.of !== 1) bad(`legs[${j}].of`);
        if (l.side !== 'L' && l.side !== 'R') bad(`legs[${j}].side`);
        if (!(num(l.gauge_m) && l.gauge_m > 0)) bad(`legs[${j}].gauge_m`);
        if (!(l.frog_s === null || (num(l.frog_s) && l.frog_s >= 0))) bad(`legs[${j}].frog_s`);
        if (!(num(l.clear_s) && l.clear_s >= 0)) bad(`legs[${j}].clear_s`);
        if (typeof l.clears !== 'boolean') bad(`legs[${j}].clears`);
      });
    }
  });
  return doc.forks;
}

// the rail graph of the drawn runs (world/ways.js railGraph's twin, on
// FORK.node_m): at = node key -> legs [{run, i, dir}], track[r] = the track
// run r lies on (runs joined end to end where only their two legs meet)
function railNet(doc, isRail) {
  const K = Math.round(1 / FORK.node_m), key = (x, z) => Math.round(x * K) + ':' + Math.round(z * K);
  const at = new Map(), track = new Int32Array(doc.ways.length).fill(-1);
  doc.ways.forEach((e, r) => {
    if (!isRail[r]) return;
    track[r] = r;
    for (let i = 0; i < e.x.length; i++) {
      const k = key(e.x[i], e.z[i]);
      if (!at.has(k)) at.set(k, []);
      if (i > 0) at.get(k).push({ run: r, i, dir: -1 });
      if (i < e.x.length - 1) at.get(k).push({ run: r, i, dir: 1 });
    }
  });
  const root = (r) => { while (track[r] !== r) r = track[r] = track[track[r]]; return r; };
  for (const L of at.values()) {
    if (L.length !== 2 || L[0].run === L[1].run) continue;
    const a = root(L[0].run), b = root(L[1].run);
    if (a !== b) track[Math.max(a, b)] = Math.min(a, b);
  }
  for (let r = 0; r < track.length; r++) if (track[r] >= 0) track[r] = root(r);
  return { at, key, track };
}

// A TRACK DRAWS AT ITS RECORDED GAUGE: forks (forkDoc's), net (railNet's),
// isRail -> {gauge: Float64Array per run (0: not a rail run), groups, mixed,
// tracks}
function _gauges(doc, forks, net, isRail) {
  const groupGauge = new Map();
  let mixed = 0;
  for (const f of forks || []) for (const l of f.legs) {
    if (!groupGauge.has(f.group)) groupGauge.set(f.group, l.gauge_m);
    else if (groupGauge.get(f.group) !== l.gauge_m) mixed++;
  }
  const trackGauge = new Map();
  for (const f of forks || []) {
    const g = groupGauge.get(f.group);
    if (g === undefined) continue;
    for (const l of [...f.through, ...f.legs]) if (!trackGauge.has(net.track[l.run])) trackGauge.set(net.track[l.run], g);
  }
  const gauge = new Float64Array(doc.ways.length);
  doc.ways.forEach((e, r) => {
    if (!isRail[r]) return;
    if (e.gauge_m !== undefined && e.gauge_m !== null) { gauge[r] = renderOf(e).rails; return; }
    const t = net ? trackGauge.get(net.track[r]) : undefined;   // no forks[] (an older world): no group
    gauge[r] = t !== undefined ? t : renderOf(e).rails;
  });
  return { gauge, groups: groupGauge.size, mixed, tracks: trackGauge.size };
}
// the rail base of a doc: its validated forks (forkDoc), isRail per run,
// the rail node net (railNet) and the gauges (_gauges). A doc with forks[]
// is resolved ONCE (memo on the doc object: ways.json is read-only after
// load), so structplan railRuns (railGauges) and ways2 (forkDraw) share one
// railNet / forkDoc pass at load whichever runs first; forks[] being an
// array, worldRev only decides a fail-fast that cannot fire. A doc without
// forks[] is not memoised (forkDoc's fail fast on worldRev every call; no
// net to build).
const _railMemo = new WeakMap();
function _railBase(doc, worldRev) {
  if (Array.isArray(doc.forks)) { const B = _railMemo.get(doc); if (B) return B; }
  const forks = forkDoc(doc, worldRev);
  const isRail = doc.ways.map((e) => renderOf(e).rails > 0);
  const net = forks ? railNet(doc, isRail) : null;
  const B = { forks, isRail, net, G: _gauges(doc, forks, net, isRail) };
  if (forks) _railMemo.set(doc, B);
  return B;
}
// THE gauge of every run (one resolution for the surface and the
// structures): doc -> Float64Array(doc.ways.length), metres, 0 for a run
// that is not a rail run; run.gauge_m, else its track's fork group gauge,
// else RENDER[cls].rails. Pure (memoised on the doc, a fresh copy per
// call); fails fast as forkDoc does (opts.worldRev).
function railGauges(doc, opts = {}) {
  return _railBase(doc, opts.worldRev).G.gauge.slice();
}

// ---- pure kernels (frames in, geometry out; no doc, no state)

// A RAIL PIECE END MEETING ANOTHER SHARES ONE SECTION: two cross-sections
// at one node -> the ONE section both take. A / B: {c: [x, y, z] the lane
// centre, n: [nx, nz] the unit normal (o +), cf the cross-fall, lo, hi};
// keep: 'A' adopts A as it is (an interior station: its run's own section),
// else the mean centre / normal / cross-fall over the common interval.
// -> {c, n, cf, lo, hi, a: [x, y, z] (o = lo), b (o = hi)} in A's
// orientation, or null (no common interval); `alignSection` gives B its copy
function sharedSection(A, B, keep = null) {
  const sg = A.n[0] * B.n[0] + A.n[1] * B.n[1] >= 0 ? 1 : -1;
  let c, n, cf, lo, hi;
  if (keep === 'A') {
    ({ c, n, cf, lo, hi } = A);
  } else {
    const nx = A.n[0] + sg * B.n[0], nz = A.n[1] + sg * B.n[1], l = Math.hypot(nx, nz) || 1;
    n = [nx / l, nz / l];
    c = [(A.c[0] + B.c[0]) / 2, (A.c[1] + B.c[1]) / 2, (A.c[2] + B.c[2]) / 2];
    cf = (A.cf + sg * B.cf) / 2;
    lo = Math.max(A.lo, sg > 0 ? B.lo : -B.hi);
    hi = Math.min(A.hi, sg > 0 ? B.hi : -B.lo);
  }
  if (!(hi > lo)) return null;
  const a = [c[0] + n[0] * lo, c[1] + cf * lo, c[2] + n[1] * lo], b = [c[0] + n[0] * hi, c[1] + cf * hi, c[2] + n[1] * hi];
  return { c, n, cf, lo, hi, a, b };
}
// the shared section as seen by a run whose own normal is `own` (the SAME
// vertex arrays, swapped when the run runs the other way)
function alignSection(sh, own) {
  if (own[0] * sh.n[0] + own[1] * sh.n[1] >= 0) return { lo: sh.lo, hi: sh.hi, a: sh.a, b: sh.b, c: sh.c, n: sh.n };
  return { lo: -sh.hi, hi: -sh.lo, a: sh.b, b: sh.a, c: sh.c, n: [-sh.n[0], -sh.n[1]] };
}

// a rail head line point: the lane centre c moved along the normal n by
// side x gauge / 2 (side -1 / +1). Bit-exact twin rule: the same c and the
// negated n with the negated side give the identical point.
function railPoint(c, n, side, gauge) {
  const off = side * gauge / 2;
  return [c[0] + n[0] * off, c[1], c[2] + n[1] * off];
}

const _d2 = (p, a, b) => {
  const dx = b[0] - a[0], dz = b[1] - a[1], L2 = dx * dx + dz * dz;
  const t = L2 > 0 ? Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dz) / L2)) : 0;
  return Math.hypot(p[0] - a[0] - t * dx, p[1] - a[1] - t * dz);
};
const _dPoly = (p, P) => { let b = Infinity; for (let j = 0; j + 1 < P.length; j++) b = Math.min(b, _d2(p, P[j], P[j + 1])); return P.length === 1 ? Math.hypot(p[0] - P[0][0], p[1] - P[0][1]) : b; };
// segment a0a1 x b0b1 -> [t along a, u along b] or null
const _cross = (a0, a1, b0, b1) => {
  const rx = a1[0] - a0[0], rz = a1[1] - a0[1], sx = b1[0] - b0[0], sz = b1[1] - b0[1];
  const den = rx * sz - rz * sx;
  if (Math.abs(den) < 1e-15) return null;
  const qx = b0[0] - a0[0], qz = b0[1] - a0[1];
  const t = (qx * sz - qz * sx) / den, u = (qx * rz - qz * rx) / den;
  return t >= 0 && t <= 1 && u >= 0 && u <= 1 ? [t, u] : null;
};

// A RAIL JUNCTION IS A TURNOUT — the rails of one fork. fork: its ways.json
// record; legs: the frames of [through[0], through[1], ...fork.legs], each
// {c: [[x, y, z]...] the lane centre per station outward from the node
// (c[0] the node), n: [[nx, nz]...] unit normals oriented alike along the
// leg, s: [...] arc length from the node} or null (not drawn); gauge (m).
// The frog's at[1] is the higher of the two lane heights at the crossing.
// -> per diverging leg null (a missing frame) or {inner, outer (the sides
// -1 / +1 along the leg's n), match {side: the through side it starts on},
// blade: [{k, p}] (the inner rail's points shifted inward until they clear
// the stock rail by FORK.rail_w: the blade lying against it from the node),
// peel: {k, t, p, n} (the outer rail starts between station k - 1 and k
// where it clears the through rail: the stations before it are not drawn)
// or {k: count, p: null} (it never clears inside the window), frog: {at,
// dir, len, w, s, theta} (the inner rail crosses the through rail on the
// diverging side) or null}
function forkRails(fork, legs, gauge) {
  const h = gauge / 2, W = FORK.rail_w;
  const rail2 = (L, sd) => L.c.map((c, k) => { const p = railPoint(c, L.n[k], sd, gauge); return [p[0], p[2]]; });
  return fork.legs.map((leg, j) => {
    const D = legs[2 + j], T = legs[leg.of];
    if (!D || !T || D.c.length < 2 || T.c.length < 2) return null;
    const TR = { [-1]: rail2(T, -1), 1: rail2(T, 1) }, DR = { [-1]: rail2(D, -1), 1: rail2(D, 1) };
    // each diverging rail starts on the nearer through rail
    const match = {};
    for (const sd of [-1, 1]) match[sd] = _dPoly(DR[sd][0], TR[-1]) <= _dPoly(DR[sd][0], TR[1]) ? -1 : 1;
    if (match[-1] === match[1]) { match[-1] = -match[1]; }
    // the inner rail: its stock rail is the farther from the leg's far end
    const end = [D.c[D.c.length - 1][0], D.c[D.c.length - 1][2]];
    const inner = _dPoly(end, TR[match[-1]]) > _dPoly(end, TR[match[1]]) ? -1 : 1, outer = -inner;
    const gap = (sd, k, P = DR[sd][k]) => _dPoly(P, TR[match[sd]]);
    // the blade: the inner rail's first stations shifted inward (toward the
    // leg's centre) until they clear the stock rail
    const blade = [];
    const IR = DR[inner].slice();
    for (let k = 0; k < D.c.length; k++) {
      const d = gap(inner, k);
      if (d >= W) break;
      const m = W - d, c = D.c[k], n = D.n[k], off = inner * (h - m);
      const p = [c[0] + n[0] * off, c[1], c[2] + n[1] * off];
      blade.push({ k, p });
      IR[k] = [p[0], p[2]];
    }
    // the outer rail parts from the through rail where it clears it
    let kp = 0;
    while (kp < D.c.length && gap(outer, kp) < W) kp++;
    let peel = null;
    if (kp >= D.c.length) peel = { k: kp, t: 0, p: null, n: null };
    else if (kp === 0) {
      // clear at the node already (a crossing, no stock rail to part from)
      peel = { k: 1, t: 0, p: railPoint(D.c[0], D.n[0], outer, gauge), n: D.n[0].slice() };
    } else {
      const d0 = gap(outer, kp - 1), d1 = gap(outer, kp), t = d1 > d0 ? (W - d0) / (d1 - d0) : 1;
      const P0 = railPoint(D.c[kp - 1], D.n[kp - 1], outer, gauge), P1 = railPoint(D.c[kp], D.n[kp], outer, gauge);
      const nx = D.n[kp - 1][0] + t * (D.n[kp][0] - D.n[kp - 1][0]), nz = D.n[kp - 1][1] + t * (D.n[kp][1] - D.n[kp - 1][1]), l = Math.hypot(nx, nz) || 1;
      peel = { k: kp, t, p: [P0[0] + t * (P1[0] - P0[0]), P0[1] + t * (P1[1] - P0[1]), P0[2] + t * (P1[2] - P0[2])], n: [nx / l, nz / l] };
    }
    // the frog: the inner rail crosses the through rail on the diverging side
    const X = TR[match[outer]];
    let frog = null;
    for (let k = 0; k + 1 < IR.length && !frog; k++) {
      for (let q = 0; q + 1 < X.length; q++) {
        const hit = _cross(IR[k], IR[k + 1], X[q], X[q + 1]);
        if (!hit) continue;
        const [t] = hit;
        const ax = IR[k + 1][0] - IR[k][0], az = IR[k + 1][1] - IR[k][1], la = Math.hypot(ax, az) || 1;
        const bx = X[q + 1][0] - X[q][0], bz = X[q + 1][1] - X[q][1], lb = Math.hypot(bx, bz) || 1;
        const dot = (ax * bx + az * bz) / (la * lb), sg = dot >= 0 ? 1 : -1;
        const theta = Math.acos(Math.min(1, Math.abs(dot)));
        const dx = ax / la + sg * bx / lb, dz = az / la + sg * bz / lb, ld = Math.hypot(dx, dz) || 1;
        // the casting's top over BOTH heads at the crossing (the leg's and the through rail's)
        const u = hit[1], yT = T.c[q][1] + u * (T.c[q + 1][1] - T.c[q][1]);
        const y = Math.max(D.c[k][1] + t * (D.c[k + 1][1] - D.c[k][1]), yT);
        const len = Math.min(FORK.frog_max_m, (theta > 1e-6 ? W / Math.sin(theta) : FORK.frog_max_m) + 2 * FORK.frog_pad_m);
        frog = { at: [IR[k][0] + t * ax, y, IR[k][1] + t * az], dir: [dx / ld, dz / ld], len, w: FORK.frog_w, s: D.s[k] + t * (D.s[k + 1] - D.s[k]), theta, k };
        break;
      }
    }
    return { inner, outer, match, blade, peel, frog };
  });
}

// ---- polygon kernels (xz, convex pieces)
const _area = (P) => { let s = 0; for (let i = 0; i < P.length; i++) { const a = P[i], b = P[(i + 1) % P.length]; s += a[0] * b[1] - b[0] * a[1]; } return s / 2; };
// the part of convex polygon P on the left (keep > 0) / right of a -> b
function _half(P, a, b, left) {
  const out = [], ex = b[0] - a[0], ez = b[1] - a[1];
  const side = (p) => { const v = ex * (p[1] - a[1]) - ez * (p[0] - a[0]); return left ? v : -v; };
  for (let i = 0; i < P.length; i++) {
    const p = P[i], q = P[(i + 1) % P.length], sp = side(p), sq = side(q);
    if (sp >= 0) out.push(p);
    if ((sp > 0 && sq < 0) || (sp < 0 && sq > 0)) { const t = sp / (sp - sq); out.push([p[0] + t * (q[0] - p[0]), p[1] + t * (q[1] - p[1])]); }
  }
  return out;
}
// convex P (CCW) and convex O (CCW) -> their intersection
function _inter(P, O) {
  let R = P;
  for (let k = 0; k < O.length && R.length >= 3; k++) R = _half(R, O[k], O[(k + 1) % O.length], true);
  return R.length >= 3 ? R : [];
}
// convex P (CCW) minus convex O (CCW) -> convex pieces
function _minus(P, O) {
  const out = [];
  let rest = P;
  for (let k = 0; k < O.length && rest.length >= 3; k++) {
    const a = O[k], b = O[(k + 1) % O.length];
    const o = _half(rest, a, b, false);
    if (o.length >= 3 && _area(o) > FORK.area_eps_m2) out.push(o);
    rest = _half(rest, a, b, true);
    if (rest.length < 3 || _area(rest) <= FORK.area_eps_m2) return out.length ? out : [];
  }
  return out;
}

// ONE BED: the triangles `tris` minus the triangles `occ` (xz). tris:
// [{v: [[x, y, z, o, s] x 3]}] (o, s: the lane offset / arc metres the client
// maps to uv), occ: [[[x, z] x 3]]. -> [{v: [[x, y, z, o, s] x 3]}], every
// new vertex on the original triangle's plane (y, o, s barycentric), the
// winding of the original kept.
function trimBed(tris, occ) {
  const out = [];
  for (const T of tris) {
    const V = T.v, P0 = V.map((p) => [p[0], p[2]]);
    const A = _area(P0);
    if (Math.abs(A) <= FORK.area_eps_m2) continue;
    const ccw = A > 0;
    let pieces = [ccw ? P0 : P0.slice().reverse()];
    const xs = P0.map((p) => p[0]), zs = P0.map((p) => p[1]);
    const bx0 = Math.min(...xs), bx1 = Math.max(...xs), bz0 = Math.min(...zs), bz1 = Math.max(...zs);
    for (const O0 of occ) {
      if (!pieces.length) break;
      const ox = O0.map((p) => p[0]), oz = O0.map((p) => p[1]);
      if (Math.max(...ox) <= bx0 || Math.min(...ox) >= bx1 || Math.max(...oz) <= bz0 || Math.min(...oz) >= bz1) continue;
      const AO = _area(O0);
      if (Math.abs(AO) <= FORK.area_eps_m2) continue;
      const O = AO > 0 ? O0 : O0.slice().reverse();
      // only a piece O really overlaps is cut (a piece merely straddling one
      // of O's edge lines stays whole)
      pieces = pieces.flatMap((p) => (_area(_inter(p, O)) > FORK.area_eps_m2 ? _minus(p, O) : [p]));
    }
    // barycentric on the original triangle
    const [a, b, c] = P0, det = (b[1] - c[1]) * (a[0] - c[0]) + (c[0] - b[0]) * (a[1] - c[1]);
    const at = (p) => {
      const l1 = ((b[1] - c[1]) * (p[0] - c[0]) + (c[0] - b[0]) * (p[1] - c[1])) / det;
      const l2 = ((c[1] - a[1]) * (p[0] - c[0]) + (a[0] - c[0]) * (p[1] - c[1])) / det, l3 = 1 - l1 - l2;
      return [p[0], l1 * V[0][1] + l2 * V[1][1] + l3 * V[2][1], p[1], l1 * V[0][3] + l2 * V[1][3] + l3 * V[2][3], l1 * V[0][4] + l2 * V[1][4] + l3 * V[2][4]];
    };
    for (const p of pieces) {
      const Q = p.map(at);
      for (let i = 1; i + 1 < Q.length; i++) {
        if (Math.abs(_area([[Q[0][0], Q[0][2]], [Q[i][0], Q[i][2]], [Q[i + 1][0], Q[i + 1][2]]])) <= FORK.area_eps_m2) continue;
        out.push({ v: ccw ? [Q[0], Q[i], Q[i + 1]] : [Q[0], Q[i + 1], Q[i]] });
      }
    }
  }
  return out;
}

// ---- the doc pass

// a leg's stations outward from the node along its track: [{refs: [{run, i,
// flip}], c, n, s}] while drawn (secOf(run, i) non-null), up to the first
// station past sMax. flip: the run's own normal = flip x the frame's.
function legWalk(doc, net, leg, sMax, secOf, frameOf) {
  const out = [];
  let run = leg.run, i = leg.i, dir = leg.dir, s = 0, prev = null, nPrev = null;
  const seen = new Set();
  for (;;) {
    const e = doc.ways[run];
    const sec = secOf(run, i);
    if (!sec) break;
    const c = frameOf(run, i, 'c'), n0 = frameOf(run, i, 'n');
    if (prev) s += Math.hypot(c[0] - prev[0], c[2] - prev[2]);
    const flip = nPrev && n0[0] * nPrev[0] + n0[1] * nPrev[1] < 0 ? -1 : 1;
    const n = flip > 0 ? n0 : [-n0[0], -n0[1]];
    if (out.length && i === leg.i && run === leg.run) break;
    const ref = { run, i, flip };
    if (prev && Math.hypot(c[0] - prev[0], c[2] - prev[2]) < FORK.node_m && out.length) {
      out[out.length - 1].refs.push(ref);   // the same node on the next run
    } else out.push({ refs: [ref], c, n, s });
    prev = c; nPrev = n;
    if (s > sMax) break;
    const j = i + dir;
    if (j >= 0 && j < e.x.length) { i = j; continue; }
    // on along the track: the node's other leg where only two legs meet
    const L = net.at.get(net.key(e.x[i], e.z[i])) || [];
    if (L.length !== 2) break;
    const nx = L.find((q) => !(q.run === run && q.i === i));
    if (!nx || seen.has(nx.run + ':' + nx.dir)) break;
    seen.add(nx.run + ':' + nx.dir);
    run = nx.run; i = nx.i; dir = nx.dir;
  }
  return out;
}

// THE FORK PASS over drawnRibbons' output -> null (no forks: legacy) or
// {keep: Uint8Array per drawn piece (0: a rail piece, redrawn here),
//  strips: [piece] (the rail ribbons, drawnRibbons' piece shape + gauge),
//  tris: [{run, cls, mat, view, texLen, w, v: [[x, y, z, o, s] x 3]}] (the
//    trimmed beds, a gap join's quad among them, tagged gap: k), rails: [{run, gauge,
//    pts: [[x, y, z, nx, nz]...], fork?, leg?, role? ('blade' | 'outer'),
//    gap?}] (rail head centre lines on the lane; the client adds its lift),
//  frogs: [{fork, leg, at: [x, y, z] (y the bed), dir: [dx, dz], len, w,
//    s (arc metres from the node), theta, k}], gauge: Float64Array per run,
//  forks: [{k, kind, drawn, on_structure, lateral (a gap left unjoined),
//  why}], stats}
function forkDraw(doc, drawn, opts = {}) { return drain(forkSteps(doc, drawn, opts)); }
function* forkSteps(doc, drawn, opts = {}) {
  const RB = _railBase(doc, opts.worldRev), forks = RB.forks;
  if (!forks) return null;
  yield;
  const runs = doc.ways, F = drawn.frames, bases = drawn.bases, ok = drawn.ok;
  const isRail = RB.isRail, net = RB.net;
  const stats = { forks: forks.length, drawn: 0, on_structure: 0, side_redrawn: 0, shared_nodes: 0, shared_fork_nodes: 0, shared_skipped: 0,
    trimmed_quads: 0, trimmed_group_quads: 0, bed_tris: 0, blades: 0, peels: 0, frogs: 0, gap_joins: 0, gap_lateral: 0, gauge_groups: 0, gauge_mixed: 0, gauge_tracks: 0, gauge_steps: 0 };
  // ---- A TRACK DRAWS AT ITS RECORDED GAUGE (railGauges' resolution)
  const GG = RB.G, gauge = GG.gauge.slice();
  stats.gauge_groups = GG.groups; stats.gauge_mixed = GG.mixed; stats.gauge_tracks = GG.tracks;
  // ---- per rail run: drawnRibbons' section of every drawn station, its caps
  const S = [], capB = [], capA = [], sArc = [];
  const init = (r) => {
    if (S[r]) return;
    const e = runs[r], n = e.x.length;
    S[r] = new Array(n).fill(null); capB[r] = new Array(n).fill(null); capA[r] = new Array(n).fill(null);
    const s = new Float64Array(n);
    for (let i = 1; i < n; i++) s[i] = s[i - 1] + Math.hypot(e.x[i] - e.x[i - 1], e.z[i] - e.z[i - 1]);
    sArc[r] = s;
  };
  const keep = new Uint8Array(drawn.pieces.length).fill(1);
  drawn.pieces.forEach((p, k) => {
    if (!isRail[p.run]) return;
    keep[k] = 0;
    init(p.run);
    p.sec.forEach((c, q) => {
      if (c.cap) { if (q === 0) capB[p.run][c.i] = c; else capA[p.run][c.i] = c; } else S[p.run][c.i] = c;
    });
  });
  const clipOf = runs.map((e) => {
    if (!e.clip || !e.clip.length) return null;
    const m = new Map();
    for (const c of e.clip) m.set(c[0], c);
    return m;
  });
  const rawSec = (r, i) => {
    const e = runs[r], h = e.w / 2;
    let lo = -h, hi = h;
    const c = clipOf[r] && clipOf[r].get(i);
    if (c) { if (c[1] === null || c[2] === null) return null; lo = Math.max(-h, c[1]); hi = Math.min(h, c[2]); }
    const nx = F[r].nx[i], nz = F[r].nz[i], y = bases[r][i], cf = e.cf[i];
    return { i, s: sArc[r][i], lo, hi, a: [e.x[i] + nx * lo, y + cf * lo, e.z[i] + nz * lo], b: [e.x[i] + nx * hi, y + cf * hi, e.z[i] + nz * hi], cap: 0 };
  };
  // ---- A RAIL IS NEVER A SIDE WAY OF A RAIL
  runs.forEach((e, r) => {
    if (!isRail[r] || !drawn.dom[r]) return;
    const n = e.x.length, R = DRAW.dom_reach;
    for (let i = 0; i < n; i++) {
      if (!ok[r][i]) continue;
      let best = null, bk = Infinity;
      for (let k = Math.max(0, i - R); k <= Math.min(n - 1, i + R); k++) {
        const d = drawn.dom[r][k];
        if (d && Math.abs(k - i) < bk) { bk = Math.abs(k - i); best = d; }
      }
      // a box any rail dominates (a level crossing's road may share it)
      if (!best || ![best.q, ...(best.alt || []).map((a) => a.q)].some((q) => isRail[q])) continue;
      init(r);
      const raw = rawSec(r, i);
      if (!raw) continue;
      const was = S[r][i];
      if (!was || was.lo !== raw.lo || was.hi !== raw.hi || capB[r][i] || capA[r][i]) stats.side_redrawn++;
      S[r][i] = raw; capB[r][i] = null; capA[r][i] = null;
      if (i > 0) capA[r][i - 1] = null;
      if (i + 1 < n) capB[r][i + 1] = null;
    }
  });
  // the lane centre / normal / cross-fall of a station's section
  const centreOf = (c) => {
    if (c.c) return c.c;
    if (c.lo <= 0 && c.hi >= 0 && c.hi > c.lo) {
      const g = -c.lo / (c.hi - c.lo);
      return [c.a[0] + g * (c.b[0] - c.a[0]), c.a[1] + g * (c.b[1] - c.a[1]), c.a[2] + g * (c.b[2] - c.a[2])];
    }
    return [(c.a[0] + c.b[0]) / 2, (c.a[1] + c.b[1]) / 2, (c.a[2] + c.b[2]) / 2];
  };
  const normalOf = (r, c) => c.n || [F[r].nx[c.i], F[r].nz[c.i]];
  const frameIn = (r, i) => {
    const c = S[r][i];
    return { c: centreOf(c), n: normalOf(r, c), cf: c.hi > c.lo ? (c.b[1] - c.a[1]) / (c.hi - c.lo) : runs[r].cf[i], lo: c.lo, hi: c.hi };
  };
  // the drawn stations of a run: interior (both neighbours drawn) / a free
  // piece end (one neighbour, no junction cap after it)
  const has = (r, i) => !!(S[r] && i >= 0 && i < runs[r].x.length && S[r][i]);
  const interior = (r, i) => has(r, i - 1) && has(r, i + 1);
  const endFree = (r, i) => has(r, i) && !interior(r, i) && (has(r, i - 1) || has(r, i + 1)) && !(has(r, i - 1) ? capA[r][i] : capB[r][i]);
  const setShared = (r, i, sh) => {
    const al = alignSection(sh, [F[r].nx[i], F[r].nz[i]]);
    S[r][i] = { i, s: sArc[r][i], lo: al.lo, hi: al.hi, a: al.a, b: al.b, cap: 0, c: al.c, n: al.n };
  };
  // ---- A RAIL PIECE END MEETING ANOTHER SHARES ONE SECTION: fork nodes first
  const done = new Set();
  const forkState = forks.map((f, k) => ({ k, kind: f.kind, drawn: false, on_structure: false, lateral: false, why: '' }));
  forks.forEach((f, k) => {
    const L = [...f.through, ...f.legs];
    if (f.kind !== 'gap' && L.some((l) => runs[l.run].flag[l.i] !== 0)) { forkState[k].on_structure = true; forkState[k].why = 'a leg\'s node station is a structure station (deck / bore): A TURNOUT ON A DECK IS DRAWN BY THE DECK\'S RAIL RUNS'; }
    if (f.kind === 'gap') return;
    const [t0, t1] = f.through;
    done.add(net.key(runs[t0.run].x[t0.i], runs[t0.run].z[t0.i]));
    if (!has(t0.run, t0.i) || !has(t1.run, t1.i)) return;
    let sh;
    if (t0.run === t1.run && t0.i === t1.i) sh = { ...frameIn(t0.run, t0.i), a: S[t0.run][t0.i].a, b: S[t0.run][t0.i].b };
    else {
      // an interior station keeps its run's own section, the other end adopts it
      const i0 = interior(t0.run, t0.i), i1 = !i0 && interior(t1.run, t1.i);
      const A = frameIn(t0.run, t0.i), B = frameIn(t1.run, t1.i);
      sh = i0 ? sharedSection(A, B, 'A') : i1 ? sharedSection(B, A, 'A') : sharedSection(A, B);
      if (!sh) { stats.shared_skipped++; return; }
      if (i0) { sh.a = S[t0.run][t0.i].a; sh.b = S[t0.run][t0.i].b; }
      if (i1) { sh.a = S[t1.run][t1.i].a; sh.b = S[t1.run][t1.i].b; }
      setShared(t0.run, t0.i, sh); setShared(t1.run, t1.i, sh);
      stats.shared_fork_nodes++;
    }
    // the diverging legs START on it (a leg passing through the node -- a
    // crossing -- keeps its own section)
    for (const l of f.legs) if (has(l.run, l.i) && !interior(l.run, l.i)) setShared(l.run, l.i, sh);
  });
  // ---- 2-leg nodes: two free piece ends
  const ends = new Map();
  runs.forEach((e, r) => {
    if (!isRail[r] || !S[r]) return;
    for (let i = 0; i < e.x.length; i++) {
      if (!endFree(r, i)) continue;
      const k = net.key(e.x[i], e.z[i]);
      if (done.has(k)) continue;
      if (!ends.has(k)) ends.set(k, []);
      ends.get(k).push([r, i]);
    }
  });
  for (const E of ends.values()) {
    if (E.length !== 2) { if (E.length > 2) stats.shared_skipped++; continue; }
    const [[ra, ia], [rb, ib]] = E;
    if (ra === rb) continue;
    const sh = sharedSection(frameIn(ra, ia), frameIn(rb, ib));
    if (!sh) { stats.shared_skipped++; continue; }
    setShared(ra, ia, sh); setShared(rb, ib, sh);
    if (gauge[ra] !== gauge[rb]) stats.gauge_steps++;
    stats.shared_nodes++;
  }
  yield;
  // ---- the pre-trim rail pieces (sections + caps) per run
  const tmplOf = (r) => { const e = runs[r], RD = renderOf(e); return { run: r, cls: e.cls, mat: RD.mat, view: RD.view, texLen: RD.texLen, rails: gauge[r], w: e.w }; };
  const pre = [];
  runs.forEach((e, r) => {
    if (!isRail[r] || !S[r]) return;
    const n = e.x.length;
    let cur = null;
    const flush = () => { if (cur && cur.length >= 2) pre.push({ ...tmplOf(r), sec: cur }); cur = null; };
    for (let i = 0; i < n; i++) {
      if (!S[r][i]) { flush(); continue; }
      if (!cur) { cur = []; if (capB[r][i]) cur.push(capB[r][i]); }
      cur.push(S[r][i]);
      if (i + 1 >= n || !S[r][i + 1]) { if (capA[r][i]) cur.push(capA[r][i]); flush(); }
    }
    flush();
  });
  yield;
  // ---- the forks: frames, rails, frog
  const secOf = (r, i) => (S[r] ? S[r][i] : null);
  const frameOf = (r, i, w) => (w === 'c' ? centreOf(S[r][i]) : normalOf(r, S[r][i]));
  const legFrames = (l, sMax) => {
    const W = legWalk(doc, net, l, sMax, secOf, frameOf);
    return W.length >= 2 ? W : null;
  };
  const rails = [], frogs = [], trimWin = [];   // trimWin: [{k, j, W}]
  const own = new Set();                         // "run:i": rail points a fork line draws (the generic lines split there)
  const line = (pts, run, g, extra = {}) => { if (pts.length >= 2) rails.push({ run, gauge: g, pts, ...extra }); };
  forks.forEach((f, k) => {
    const st = forkState[k];
    if (f.kind === 'gap' || st.on_structure) return;   // a deck turnout: structplan railRuns draws it
    const g = gauge[f.legs[0].run];
    const fr = [legFrames(f.through[0], f.clear_m), legFrames(f.through[1], f.clear_m), ...f.legs.map((l) => legFrames(l, l.clear_s))];
    const asLeg = (W) => (W ? { c: W.map((q) => q.c), n: W.map((q) => q.n), s: W.map((q) => q.s) } : null);
    const R = forkRails(f, fr.map(asLeg), g);
    let all = !!(fr[0] && fr[1]);
    R.forEach((res, j) => {
      const W = fr[2 + j], l = f.legs[j];
      if (!res) { all = false; return; }
      trimWin.push({ k, j, W });
      // the fork's own stretch: the blade and the outer rail's parting
      const kOwn = Math.min(W.length - 1, Math.max(1, res.blade.length, res.peel.p ? res.peel.k : W.length - 1));
      for (let q = 1; q < kOwn; q++) for (const ref of W[q].refs) own.add(ref.run + ':' + ref.i);
      if (!has(l.run, l.i - l.dir)) own.add(l.run + ':' + l.i);
      const P = (q, sd) => { const p = railPoint(W[q].c, W[q].n, sd, g); return [p[0], p[1], p[2], W[q].n[0], W[q].n[1]]; };
      const inner = [];
      for (let q = 0; q <= kOwn; q++) {
        const b = res.blade[q];
        inner.push(b && b.k === q ? [b.p[0], b.p[1], b.p[2], W[q].n[0], W[q].n[1]] : P(q, res.inner));
      }
      line(inner, l.run, g, { fork: k, leg: j, role: 'blade' });
      if (res.blade.length) stats.blades++;
      if (res.peel.p) {
        const outer = [[res.peel.p[0], res.peel.p[1], res.peel.p[2], res.peel.n[0], res.peel.n[1]]];
        for (let q = res.peel.k; q <= kOwn; q++) outer.push(P(q, res.outer));
        line(outer, l.run, g, { fork: k, leg: j, role: 'outer' });
        stats.peels++;
      }
      if (res.frog) { frogs.push({ fork: k, leg: j, ...res.frog }); stats.frogs++; } else if (l.frog_s !== null) all = false;
    });
    st.drawn = all;
    if (!all && !st.why) st.why = 'a leg is not drawn at the node or its frog is not inside the drawn window';
  });
  yield;
  // the generic rails: two head lines per pre-trim piece, split where a fork
  // line draws (a shared section gives both runs the identical head points)
  pre.forEach((p) => {
    for (const sd of [-1, 1]) {
      let cur = [];
      for (const c of p.sec) {
        if (!c.cap && own.has(p.run + ':' + c.i)) { line(cur, p.run, p.rails); cur = []; continue; }
        const n = normalOf(p.run, c), q = railPoint(centreOf(c), n, sd, p.rails);
        cur.push([q[0], q[1], q[2], n[0], n[1]]);
      }
      line(cur, p.run, p.rails);
    }
  });
  // ---- ONE BED: the strips' triangles, then each diverging window trimmed
  const quadKey = (r, i) => r + ':' + i;
  const trimQ = new Set();
  for (const T of trimWin) {
    for (let q = 0; q + 1 < T.W.length; q++) {
      // the quad between frame q and q + 1 lies on the run of q + 1's first ref
      const B = T.W[q + 1].refs[0], A = T.W[q].refs.find((ref) => ref.run === B.run);
      if (!A || Math.abs(A.i - B.i) !== 1) continue;
      trimQ.add(quadKey(B.run, Math.min(A.i, B.i)));
    }
  }
  const strips = [], index = new Map(), triAll = [];
  const cell = (x) => Math.floor(x / FORK.index_m);
  const cells = (P, fn) => {
    const x0 = cell(Math.min(P[0][0], P[1][0], P[2][0])), x1 = cell(Math.max(P[0][0], P[1][0], P[2][0]));
    const z0 = cell(Math.min(P[0][2], P[1][2], P[2][2])), z1 = cell(Math.max(P[0][2], P[1][2], P[2][2]));
    for (let gx = x0; gx <= x1; gx++) for (let gz = z0; gz <= z1; gz++) fn(gx + ':' + gz);
  };
  const addTri = (t) => {
    t.id = triAll.length; triAll.push(t); t.alive = true;
    cells(t.v, (kk) => { if (!index.has(kk)) index.set(kk, []); index.get(kk).push(t.id); });
  };
  // the live triangles around t of the other runs (own: its own run's too,
  // but not its own quad's: a gap join against its dead ends)
  const near = (t, own = false) => {
    const seen = new Set(), out = [];
    cells(t.v, (kk) => {
      for (const id of index.get(kk) || []) {
        if (seen.has(id)) continue;
        seen.add(id);
        const o = triAll[id];
        if (o.alive && (o.run !== t.run || (own && o.quad !== t.quad))) out.push(o);
      }
    });
    return out;
  };
  const quadTris = (A, B) => [[A.a, A.lo, A.s], [A.b, A.hi, A.s], [B.a, B.lo, B.s], [B.b, B.hi, B.s]];
  // every quad's two triangles; hi(k) -> lo(k+1): (a0, b0, a1), (b0, b1, a1)
  const quads = [];
  for (let pk = 0; pk < pre.length; pk++) {
    yield;
    const p = pre[pk];
    for (let q = 0; q + 1 < p.sec.length; q++) {
      const A = p.sec[q], B = p.sec[q + 1];
      const key = !A.cap && !B.cap ? quadKey(p.run, Math.min(A.i, B.i)) : 'cap:' + pk + ':' + q;
      const [a0, b0, a1, b1] = quadTris(A, B).map(([v, o, s]) => [v[0], v[1], v[2], o, s]);
      const T = [[a0, b0, a1], [b0, b1, a1]].map((v) => ({ run: p.run, v, quad: key, strip: true, tmpl: p }));
      T.forEach(addTri);
      quads.push({ pk, q, key, T });
    }
  }
  // ONE BALLAST BED PER GROUP: beyond the diverging windows, a quad whose bed
  // overlaps another run's (a ladder's neighbouring tracks, a leg past its
  // clear point) is trimmed too, in (run, station) order
  const overlaps = (t, o) => {
    let P = t.v.map((v) => [v[0], v[2]]), O = o.v.map((v) => [v[0], v[2]]);
    if (_area(P) < 0) P = P.reverse();
    if (_area(O) < 0) O = O.reverse();
    return _area(_inter(P, O)) > FORK.overlap_eps_m2;
  };
  const extraQ = [];
  for (let qi = 0; qi < quads.length; qi++) {
    if ((qi & 15) === 15) yield;
    const Q = quads[qi];
    if (trimQ.has(Q.key)) continue;
    if (Q.T.some((t) => near(t).some((o) => !trimQ.has(o.quad) && overlaps(t, o)))) extraQ.push(Q.key);
  }
  for (const key of extraQ) trimQ.add(key);
  // the strips: each piece's quads that no trim touches
  let cur = null, curP = -1;
  const flush = () => { if (cur && cur.length >= 2) strips.push({ ...pre[curP], sec: cur }); cur = null; };
  for (const Q of quads) {
    if (Q.pk !== curP) { flush(); curP = Q.pk; }
    if (trimQ.has(Q.key)) { for (const t of Q.T) t.strip = false; flush(); continue; }
    const p = pre[Q.pk];
    if (!cur) cur = [p.sec[Q.q]];
    else if (cur[cur.length - 1] !== p.sec[Q.q]) { flush(); cur = [p.sec[Q.q]]; }
    cur.push(p.sec[Q.q + 1]);
  }
  flush();
  // trim each window in fork / leg order, then the other overlapping quads,
  // each against what is drawn now
  const qTris = new Map();
  for (const t of triAll) if (!t.strip) { if (!qTris.has(t.quad)) qTris.set(t.quad, []); qTris.get(t.quad).push(t); }
  const trimOne = (key, own = false) => {
    const cur = (qTris.get(key) || []).filter((t) => t.alive);
    if (!cur.length) return false;
    for (const t of cur) t.alive = false;
    const res = [];
    for (const t of cur) {
      const occ = near(t, own).map((o) => o.v.map((v) => [v[0], v[2]]));
      for (const r of trimBed([{ v: t.v }], occ)) res.push({ run: t.run, v: r.v, quad: key, strip: false, tmpl: t.tmpl });
    }
    for (const r of res) addTri(r);
    qTris.set(key, res);
    return true;
  };
  const doneQ = new Set();
  for (const T of trimWin) {
    yield;
    for (let q = 0; q + 1 < T.W.length; q++) {
      const B = T.W[q + 1].refs[0], A = T.W[q].refs.find((ref) => ref.run === B.run);
      if (!A || Math.abs(A.i - B.i) !== 1) continue;
      const key = quadKey(B.run, Math.min(A.i, B.i));
      if (doneQ.has(key)) continue;
      if (trimOne(key)) { doneQ.add(key); stats.trimmed_quads++; }
    }
  }
  for (const key of extraQ) {
    yield;
    if (!doneQ.has(key) && trimOne(key)) { doneQ.add(key); stats.trimmed_group_quads++; }
  }
  // ---- A GAP IS ONE BED: the two dead ends joined (one quad, one rail segment per rail)
  forks.forEach((f, k) => {
    if (f.kind !== 'gap') return;
    const [ga, gb] = f.through;
    const A = secOf(ga.run, ga.i), B = secOf(gb.run, gb.i);
    if (!A || !B) { forkState[k].why = forkState[k].why || 'a dead end is not drawn'; return; }
    const nA = normalOf(ga.run, A), nB = normalOf(gb.run, B);
    const sg = nA[0] * nB[0] + nA[1] * nB[1] >= 0 ? 1 : -1;
    const cA = centreOf(A), cB = centreOf(B), d = Math.hypot(cB[0] - cA[0], cB[2] - cA[2]);
    // only a FACING gap is one track: each end's outward tangent (from its
    // next station into the gap) within FORK.gap_face_deg of the line to the
    // other end; side-by-side dead ends stay unjoined
    const outward = (l) => { const e = runs[l.run], j = l.i + l.dir, tx = e.x[l.i] - e.x[j], tz = e.z[l.i] - e.z[j], tl = Math.hypot(tx, tz) || 1; return [tx / tl, tz / tl]; };
    if (d > 1e-9) {
      const tA = outward(ga), tB = outward(gb), vx = (cB[0] - cA[0]) / d, vz = (cB[2] - cA[2]) / d;
      const offA = Math.acos(Math.max(-1, Math.min(1, tA[0] * vx + tA[1] * vz))) * 180 / Math.PI, offB = Math.acos(Math.max(-1, Math.min(1, -(tB[0] * vx + tB[1] * vz)))) * 180 / Math.PI;
      if (offA > FORK.gap_face_deg || offB > FORK.gap_face_deg) {
        forkState[k].lateral = true;
        forkState[k].why = `a lateral gap: the line between the dead ends is ${offA.toFixed(1)} / ${offB.toFixed(1)} deg off their outward tangents (> FORK.gap_face_deg ${FORK.gap_face_deg}): two tracks side by side, not joined (A GAP IS ONE BED only between facing ends)`;
        stats.gap_lateral++;
        return;
      }
    }
    const s1 = A.s + (ga.dir < 0 ? d : -d);
    const B2 = sg > 0 ? { i: A.i, s: s1, lo: B.lo, hi: B.hi, a: B.a, b: B.b, cap: 0 } : { i: A.i, s: s1, lo: -B.hi, hi: -B.lo, a: B.b, b: B.a, cap: 0 };
    const A2 = { ...A };
    const sec = ga.dir < 0 ? [A2, B2] : [B2, A2];
    // the join is trimmed like a window (its bed may cross a neighbour's)
    const [a0, b0, a1, b1] = quadTris(sec[0], sec[1]).map(([v, o, s]) => [v[0], v[1], v[2], o, s]);
    const gk = 'gap:' + k, tp = { ...tmplOf(ga.run), gap: k };
    const gT = [[a0, b0, a1], [b0, b1, a1]].map((v) => ({ run: ga.run, v, quad: gk, strip: false, tmpl: tp }));
    gT.forEach(addTri);
    qTris.set(gk, gT);
    trimOne(gk, true);
    // rails: each of A's head points to B's on the same side
    for (const sd of [-1, 1]) {
      const pa = railPoint(cA, nA, sd, gauge[ga.run]), pb = railPoint(cB, nB, sd * sg, gauge[gb.run]);
      const nb = sg > 0 ? nB : [-nB[0], -nB[1]];
      rails.push({ run: ga.run, gauge: gauge[ga.run], gap: k, pts: [[pa[0], pa[1], pa[2], nA[0], nA[1]], [pb[0], pb[1], pb[2], nb[0], nb[1]]] });
    }
    forkState[k].drawn = true;
    stats.gap_joins++;
  });
  const tris = [];
  for (const t of triAll) if (t.alive && !t.strip) { const p = t.tmpl, o = { run: t.run, cls: p.cls, mat: p.mat, view: p.view, texLen: p.texLen, w: p.w, v: t.v }; if (p.gap !== undefined) o.gap = p.gap; tris.push(o); }
  stats.bed_tris = tris.length;
  for (const st of forkState) { if (st.drawn) stats.drawn++; else if (st.on_structure) stats.on_structure++; }
  return { keep, strips, tris, rails, frogs, gauge, forks: forkState, stats };
}

// ------------------------------------------------------------ near 3D detail
// WAYS-G L-h (waysg CONTRACT O4 / O5, W3): the ground draws the ways; what is
// genuinely 3D near the eye is placed here (pure; client/src/ways2.js
// instances it, scripts/check-pathfield.mjs gates it against the field).
//   sleepers: on every ground rail piece the field draws (the kept pieces +
//     forkDraw's strips — world/pathfield.mjs's sources), one per sleeper
//     pitch at s = k x pitch (the field's phase phi = s / pitch: a sleeper
//     is centred where phi is whole), centred on the section middle (the
//     field's o origin), across the run; its top `top_m` over the ballast top
//     (the lane surface, W2). A sleeper within `dedup_m` of one already
//     placed (a turnout: two runs on one bed) is not doubled. Length
//     min(look sleeper_len_m, doc.ballast.top_w_m - 2 x shoulder_m).
//   kerbs: along the carriageway bound of every sidewalk (paintParts u: a
//     left sidewalk where u0 > 0, its kerb at u1; a right one where u3 < 1,
//     at u2), the face doc.kerb.h_m over the carriageway edge, the stone
//     PAINT.kerb_line_m wide on the sidewalk side, one box per section quad.
// Only where W's shapes exist (doc.ballast / doc.kerb): without the prism /
// the raise a box would stand on ground that is not there. A doc with
// doc.walks (WAYSG2 P1) gets NO kerb boxes: e.sw strips would cross the road
// mouths the walks open; the kerb is the walk's own raise (the corridor's
// walk top, h_m over the carriageway at the kerb line) — and, with the L0
// pave re-mesh, its vertical h_m step. opts.kerbs: false = none, true = the
// e.sw boxes even then (the gates' red twin).
// look = {sleeper_pitch_m, sleeper_len_m, sleeper_w_m} (wayslook ballast).
// -> {sleepers: Float64Array 7 per sleeper [x, y (top), z, ax, az (unit,
//      across), len, run], kerbs: Float64Array 10 per box [x0, y0, z0, x1,
//      y1, z1 (the face line, top = y + h), sx, sz (unit, toward the
//      sidewalk), w, run], h_m, stats}
const DETAIL = {
  top_m: 0.04,            // sleeper top over the ballast top (the crib ballast sits just under it)
  depth_m: 0.2,           // sleeper / kerb box depth below its top (buried: no bottom face drawn)
  shoulder_m: 0.15,       // ballast shoulder kept clear at each sleeper end
  dedup_m: 1.0,           // a sleeper centre this close to a placed one (another run's) is not doubled
  kerb_below_m: 0.05,     // the kerb face reaches this far under the carriageway edge
};
function nearDetail(doc, drawn, paint, forks, look, opts = {}) {
  const runs = doc.ways, B = doc.ballast || null, K = doc.kerb || null;
  const walksMode = !!(doc.walks && Array.isArray(doc.walks.items));
  const stats = { sleepers: 0, sleeper_dedup: 0, kerbs: 0, kerb_m: 0, ballast: !!B, kerb: !!K, kerb_from: walksMode ? 'walks' : 'sw' };
  const sl = [], kb = [];
  if (B) {
    const pitch = look.sleeper_pitch_m;
    if (!(pitch > 0) || !(look.sleeper_len_m > 0)) throw new Error('nearDetail: look needs sleeper_pitch_m, sleeper_len_m');
    if (!(B.top_w_m > 0)) throw new Error('nearDetail: doc.ballast.top_w_m');
    const len = Math.min(look.sleeper_len_m, B.top_w_m - 2 * DETAIL.shoulder_m);
    const src = [];
    drawn.pieces.forEach((p, pi) => { if (p.ground && (!forks || forks.keep[pi]) && runs[p.run].cls === 'rail') src.push(p); });
    if (forks) for (const p of forks.strips) if (runs[p.run] && runs[p.run].cls === 'rail') src.push(p);
    const G = 2, grid = new Map();
    const near = (x, z, run) => {
      const i = Math.floor(x / G), j = Math.floor(z / G);
      for (let a = -1; a <= 1; a++) for (let b = -1; b <= 1; b++) {
        const L = grid.get((i + a) + ',' + (j + b));
        if (L) for (const q of L) if (q[2] !== run && Math.hypot(q[0] - x, q[1] - z) < DETAIL.dedup_m) return true;
      }
      return false;
    };
    for (const p of src) {
      const S = p.sec;
      for (let q = 0; q + 1 < S.length; q++) {
        const A = S[q], C = S[q + 1];
        if (!(C.s > A.s)) continue;
        const last = q + 2 === S.length;
        const ma = [(A.a[0] + A.b[0]) / 2, (A.a[1] + A.b[1]) / 2, (A.a[2] + A.b[2]) / 2];
        const mc = [(C.a[0] + C.b[0]) / 2, (C.a[1] + C.b[1]) / 2, (C.a[2] + C.b[2]) / 2];
        const dx = mc[0] - ma[0], dz = mc[2] - ma[2], dl = Math.hypot(dx, dz);
        if (dl < 1e-6) continue;
        for (let k = Math.ceil(A.s / pitch - 1e-9); k * pitch < C.s || (last && k * pitch <= C.s + 1e-9); k++) {
          const f = (k * pitch - A.s) / (C.s - A.s);
          const x = ma[0] + f * dx, y = ma[1] + f * (mc[1] - ma[1]), z = ma[2] + f * dz;
          if (near(x, z, p.run)) { stats.sleeper_dedup++; continue; }
          const key = Math.floor(x / G) + ',' + Math.floor(z / G);
          if (!grid.has(key)) grid.set(key, []);
          grid.get(key).push([x, z, p.run]);
          // across the run: the travel direction turned left
          sl.push(x, y + DETAIL.top_m, z, -dz / dl, dx / dl, len, p.run);
        }
      }
    }
    stats.sleepers = sl.length / 7;
  }
  if (K && paint && (opts.kerbs === true || (opts.kerbs !== false && !walksMode))) {
    if (!(K.h_m > 0)) throw new Error('nearDetail: doc.kerb.h_m');
    const w = PAINT.kerb_line_m;
    drawn.pieces.forEach((p, pi) => {
      const part = paint.parts[pi];
      if (!p.ground || !part || !part.u) return;
      const e = runs[p.run], W = e.w, h = W / 2, S = p.sec, u = part.u;
      // the point at offset o of section X (a at lo, b at hi; y across too)
      const at = (X, o) => {
        if (!(X.hi > X.lo) || o < X.lo - 1e-6 || o > X.hi + 1e-6) return null;
        const f = (o - X.lo) / (X.hi - X.lo);
        return [X.a[0] + f * (X.b[0] - X.a[0]), X.a[1] + f * (X.b[1] - X.a[1]), X.a[2] + f * (X.b[2] - X.a[2])];
      };
      for (const side of [-1, 1]) {
        const has = (q) => (side < 0 ? u[4 * q] > 1e-6 : u[4 * q + 3] < 1 - 1e-6);
        const off = (q) => -h + (side < 0 ? u[4 * q + 1] : u[4 * q + 2]) * W;
        for (let q = 0; q + 1 < S.length; q++) {
          if (!has(q) || !has(q + 1)) continue;
          const P0 = at(S[q], off(q)), P1 = at(S[q + 1], off(q + 1));
          if (!P0 || !P1) continue;
          const dx = P1[0] - P0[0], dz = P1[2] - P0[2], dl = Math.hypot(dx, dz);
          if (dl < 1e-6) continue;
          // toward the sidewalk: -o (the section's a side) for the left one
          const X = S[q], ax = X.a[0] - X.b[0], az = X.a[2] - X.b[2], al = Math.hypot(ax, az) || 1;
          const sx = side < 0 ? ax / al : -ax / al, sz = side < 0 ? az / al : -az / al;
          kb.push(P0[0], P0[1], P0[2], P1[0], P1[1], P1[2], sx, sz, w, p.run);
          stats.kerb_m += dl;
        }
      }
    });
    stats.kerbs = kb.length / 10;
    stats.kerb_m = Math.round(stats.kerb_m);
  }
  return { sleepers: Float64Array.from(sl), kerbs: Float64Array.from(kb), h_m: K ? K.h_m : 0, stats };
}

// (D1-TUNC) THE TRACK CONTINUES INTO THE BORE: the sleepers of every rail
// run's BORE stretch, placed like nearDetail's ground sleepers (the same
// look pitch / length, DETAIL.top_m over the run's lane y, across the
// travel direction, s = k x pitch of the run's own plan arc so the phase
// runs on through the portal, DETAIL.dedup_m between runs). A stretch is a
// run's contiguous bore stations plus the non-drop station on either side
// (railRuns' rail stretch); a sleeper lies strictly between those
// neighbour stations' s (the ground piece ending there draws its own,
// k x pitch <= s inclusive) and at a run end inclusive. nearDetail and the
// ground sleepers are untouched (client/src/structplan.js draws these in
// the bore batch, near the player only). Only where doc.ballast exists.
// look = {sleeper_pitch_m, sleeper_len_m}. -> {ties: Float64Array 8 per
// sleeper [x, y (top), z, ax, az (unit, across), len, run, i (the station
// the sleeper's segment starts at)], stats}
function boreTies(doc, look) {
  const runs = doc.ways || [], B = doc.ballast || null;
  const stats = { ties: 0, tie_dedup: 0, stretches: 0, ballast: !!B };
  const out = [];
  if (!B) return { ties: new Float64Array(0), stats };
  const pitch = look.sleeper_pitch_m;
  if (!(pitch > 0) || !(look.sleeper_len_m > 0)) throw new Error('boreTies: look needs sleeper_pitch_m, sleeper_len_m');
  if (!(B.top_w_m > 0)) throw new Error('boreTies: doc.ballast.top_w_m');
  const len = Math.min(look.sleeper_len_m, B.top_w_m - 2 * DETAIL.shoulder_m);
  const K = (doc.codes && doc.codes.kind) || ['surface', 'deck', 'bore', 'drop'];
  const BORE = K.indexOf('bore'), DROP = K.indexOf('drop');
  const G = 2, grid = new Map();
  const near = (x, z, run) => {
    const i = Math.floor(x / G), j = Math.floor(z / G);
    for (let a = -1; a <= 1; a++) for (let b = -1; b <= 1; b++) {
      const L = grid.get((i + a) + ',' + (j + b));
      if (L) for (const q of L) if (q[2] !== run && Math.hypot(q[0] - x, q[1] - z) < DETAIL.dedup_m) return true;
    }
    return false;
  };
  runs.forEach((e, r) => {
    if (e.cls !== 'rail' || !Array.isArray(e.x) || e.x.length < 2) return;
    const n = e.x.length, s = new Float64Array(n);
    for (let i = 1; i < n; i++) s[i] = s[i - 1] + Math.hypot(e.x[i] - e.x[i - 1], e.z[i] - e.z[i - 1]);
    let i = 0;
    while (i < n) {
      if (e.kind[i] !== BORE) { i++; continue; }
      let j = i;
      while (j + 1 < n && e.kind[j + 1] === BORE) j++;
      const a = i > 0 && e.kind[i - 1] !== DROP ? i - 1 : i;
      const b = j + 1 < n && e.kind[j + 1] !== DROP ? j + 1 : j;
      stats.stretches++;
      const lo = s[a], hi = s[b];
      for (let k = Math.ceil(lo / pitch - 1e-9); k * pitch <= hi + 1e-9; k++) {
        const t = k * pitch;
        if ((a < i && t <= lo + 1e-9) || (b > j && t >= hi - 1e-9)) continue;
        let q = a;
        while (q + 1 < b && s[q + 1] < t) q++;
        const dl = s[q + 1] - s[q];
        if (!(dl > 1e-6)) continue;
        const f = Math.max(0, Math.min(1, (t - s[q]) / dl));
        const dx = e.x[q + 1] - e.x[q], dz = e.z[q + 1] - e.z[q];
        const x = e.x[q] + f * dx, z = e.z[q] + f * dz, y = e.y[q] + f * (e.y[q + 1] - e.y[q]);
        if (near(x, z, r)) { stats.tie_dedup++; continue; }
        const key = Math.floor(x / G) + ',' + Math.floor(z / G);
        if (!grid.has(key)) grid.set(key, []);
        grid.get(key).push([x, z, r]);
        out.push(x, y + DETAIL.top_m, z, -dz / dl, dx / dl, len, r, q);
      }
      i = j + 1;
    }
  });
  stats.ties = out.length / 8;
  return { ties: Float64Array.from(out), stats };
}

// WAYSG-L2 rails: the head line's chord tolerance and longest kept segment
// (client/src/ways2.js _forkParts; check-draw gates both)
const RAIL_DP = { eps_m: 0.01, max_m: 64 };
// the kept point indices of a [x, y, z, ...] polyline: 3D Douglas-Peucker
// (every dropped point within eps of its chord), first / last always, and a
// forced point once the run since the last forced one passes max_m
function railKeep(P, eps, maxM) {
  const n = P.length;
  if (n < 3) return n === 2 ? [0, 1] : n ? [0] : [];
  const keep = new Uint8Array(n);
  keep[0] = keep[n - 1] = 1;
  for (let i = 1, run = 0; i < n - 1; i++) {
    run += Math.hypot(P[i][0] - P[i - 1][0], P[i][2] - P[i - 1][2]);
    if (run + Math.hypot(P[i + 1][0] - P[i][0], P[i + 1][2] - P[i][2]) > maxM) { keep[i] = 1; run = 0; }
  }
  const forced = [];
  for (let i = 0; i < n; i++) if (keep[i]) forced.push(i);
  const st = [];
  for (let q = 0; q + 1 < forced.length; q++) st.push([forced[q], forced[q + 1]]);
  while (st.length) {
    const [i, j] = st.pop();
    if (j <= i + 1) continue;
    const A = P[i], B = P[j], dx = B[0] - A[0], dy = B[1] - A[1], dz = B[2] - A[2];
    const l2 = dx * dx + dy * dy + dz * dz || 1;
    let worst = -1, wk = -1;
    for (let k = i + 1; k < j; k++) {
      const p = P[k], ex = p[0] - A[0], ey = p[1] - A[1], ez = p[2] - A[2];
      const t = Math.max(0, Math.min(1, (ex * dx + ey * dy + ez * dz) / l2));
      const e = Math.hypot(ex - t * dx, ey - t * dy, ez - t * dz);
      if (e > worst) { worst = e; wk = k; }
    }
    if (worst > eps) { keep[wk] = 1; st.push([i, wk], [wk, j]); }
  }
  const out = [];
  for (let i = 0; i < n; i++) if (keep[i]) out.push(i);
  return out;
}

module.exports = { WAYS_FORMAT, WAYS_SCHEMA, DRAW_REV, DRAW, RENDER, PAINT, FORK, DETAIL, nearDetail, boreTies, checkWays, surfHalves, renderOf, baseOf, flightPlane, isFlightRun, flightFoot, flightLaneOf, chainJoins, runFrames,
  laneClip, segAt, isGroundPath, surfaceKind, wayKind, isGroundWay, weakBox, drawnRibbons, tri, MARKS, XLINE_SUB, TEETH_HB0, teethCount, teethWidth, teethBack, teethAt, markGeom, markLocal, zebraBars, laneLayout, laneLines, laneMarks, deckRibbons, pieceTaps, pieceCentre, graphInput, paintDoc, paintParts, insetPoly, islandEdgeY,
  forkDoc, railNet, legWalk, sharedSection, alignSection, railPoint, forkRails, trimBed, forkDraw, railGauges, railKeep, RAIL_DP,
  drain, drawnSteps, forkSteps, laneSteps, paintSteps, graphSteps };
